import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import {
  LayoutDashboard, Calendar, FileDown, Settings, ChevronLeft, ChevronRight,
  Check, X, Clock, FileText, Upload, Download, Eye, Users, Briefcase, Filter,
  Plus, Mail, CalendarDays, AlertTriangle, Shield, ArrowRight, Trash2, Building2, FolderOpen, Info, Bell, UserCheck, UserCog, Edit3, Palmtree
} from "lucide-react";
import DocumentsTab from "./DocumentsTab";
import BalancesTab from "./BalancesTab";
import AdminCreateRequestModal from "@/components/admin/AdminCreateRequestModal";
import { formatDateES } from "@/lib/dateUtils";
import { useYearRange } from "@/hooks/useYearRange";

const API = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════
// DASHBOARD TAB
// ═══════════════════════════════════════════
function DashboardTab({ axiosAuth, hrConfig, onKpiClick, activeKpi }) {
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);

  const load = useCallback(async () => {
    try {
      const [dashRes, alertsRes] = await Promise.all([
        axiosAuth().get("/hr/dashboard"),
        axiosAuth().get("/hr/alerts", { params: { resolved: "false" } }),
      ]);
      setData(dashRes.data);
      setAlerts(alertsRes.data);
    } catch { toast.error("Error al cargar dashboard"); }
  }, [axiosAuth]);

  useEffect(() => { load(); }, [load]);

  const stateMap = {};
  (hrConfig?.leave_states || []).forEach(s => { stateMap[s.value] = s; });
  const typeMap = {};
  (hrConfig?.request_types || []).forEach(t => { typeMap[t.value] = t; });

  const resolveAlert = async (id) => {
    try {
      await axiosAuth().put(`/hr/alerts/${id}/resolve`);
      load();
    } catch { toast.error("Error"); }
  };

  if (!data) return <div className="text-center py-8 text-slate-400 text-sm">Cargando...</div>;

  return (
    <div className="space-y-5" data-testid="hr-dashboard-tab">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { id: "pendientes", label: "Pendientes", value: data.pending_requests, color: "text-amber-600 bg-amber-50 border-amber-200", activeColor: "ring-2 ring-amber-500 ring-offset-2" },
          { id: "ausentes", label: "Ausentes hoy", value: data.active_today, color: "text-blue-600 bg-blue-50 border-blue-200", activeColor: "ring-2 ring-blue-500 ring-offset-2" },
          { id: "semana", label: "Semana", value: data.weekly_absences, color: "text-indigo-600 bg-indigo-50 border-indigo-200", activeColor: "ring-2 ring-indigo-500 ring-offset-2" },
          { id: "bajas", label: "Bajas activas", value: data.active_bajas, color: "text-red-600 bg-red-50 border-red-200", activeColor: "ring-2 ring-red-500 ring-offset-2" },
          { id: "alertas", label: "Alertas", value: data.open_alerts, color: data.open_alerts > 0 ? "text-orange-700 bg-orange-50 border-orange-300" : "text-green-600 bg-green-50 border-green-200", activeColor: "ring-2 ring-orange-500 ring-offset-2" },
          { id: "docs", label: "Docs por validar", value: data.docs_pending_validation || 0, color: (data.docs_pending_validation || 0) > 0 ? "text-orange-700 bg-orange-50 border-orange-300" : "text-green-600 bg-green-50 border-green-200", activeColor: "ring-2 ring-orange-500 ring-offset-2" },
        ].map(k => (
          <Card
            key={k.id}
            className={`border rounded-sm cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${k.color} ${activeKpi === k.id ? k.activeColor : ""}`}
            onClick={() => onKpiClick?.(k.id)}
            data-testid={`kpi-${k.id}`}
          >
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold font-mono">{k.value}</p>
              <p className="text-[10px] uppercase tracking-wider font-semibold mt-1">{k.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Ausentes hoy */}
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider font-semibold flex items-center gap-2"><Users className="w-4 h-4 text-blue-600" /> Ausentes hoy</CardTitle></CardHeader>
          <CardContent>
            {(data.active_today_list || []).length === 0 ? (
              <p className="text-xs text-slate-400 py-4 text-center">Nadie ausente hoy</p>
            ) : (
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {data.active_today_list.map(r => (
                  <div key={r.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-sm text-xs">
                    <div>
                      <span className="font-semibold">{r.employee_name}</span>
                      <span className="text-slate-400 ml-2">{r.center}</span>
                    </div>
                    <Badge variant="outline" className="text-[9px]" style={{ color: hrConfig?.calendar_colors?.[r.request_type] || "#94a3b8" }}>
                      {typeMap[r.request_type]?.label || r.request_type}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Alertas abiertas */}
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider font-semibold flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-orange-500" /> Alertas abiertas</CardTitle></CardHeader>
          <CardContent>
            {alerts.length === 0 ? (
              <p className="text-xs text-slate-400 py-4 text-center">Sin alertas</p>
            ) : (
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {alerts.slice(0, 8).map(a => (
                  <div key={a.id} className={`flex items-start gap-2 p-2 rounded-sm text-xs border ${a.severity === "critical" ? "bg-red-50 border-red-200" : a.severity === "block" ? "bg-red-100 border-red-300" : "bg-amber-50 border-amber-200"}`}>
                    <AlertTriangle className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${a.severity === "critical" || a.severity === "block" ? "text-red-500" : "text-amber-500"}`} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{a.message}</p>
                      <p className="text-[10px] text-slate-400">{a.type} - {formatDateES(a.created_at)}</p>
                    </div>
                    <Button variant="ghost" size="sm" className="h-5 text-[9px] px-1.5" onClick={() => resolveAlert(a.id)} data-testid={`resolve-alert-${a.id}`}>
                      <Check className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Estadisticas por tipo */}
      {Object.keys(data.type_stats || {}).length > 0 && (
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider font-semibold">Resumen del mes</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {Object.entries(data.type_stats).map(([type, stats]) => (
                <div key={type} className="p-2 border border-slate-100 rounded-sm text-center">
                  <div className="w-3 h-3 rounded-full mx-auto mb-1" style={{ backgroundColor: hrConfig?.calendar_colors?.[type] || "#94a3b8" }} />
                  <p className="text-[10px] font-semibold uppercase">{typeMap[type]?.label || type}</p>
                  <p className="text-lg font-bold font-mono">{stats.total}</p>
                  <p className="text-[9px] text-slate-400">{stats.days} dias | {stats.pending} pend.</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Actividad reciente */}
      <Card className="border-slate-200 rounded-sm">
        <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider font-semibold">Actividad reciente</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
            {(data.recent_activity || []).map(r => (
              <div key={r.id} className="flex items-center gap-3 px-4 py-2.5 text-xs hover:bg-slate-50">
                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: stateMap[r.status]?.color || "#94a3b8" }} />
                <span className="font-medium">{r.employee_name}</span>
                <span className="text-slate-400">{typeMap[r.request_type]?.label || r.request_type}</span>
                <Badge variant="outline" className="text-[9px] ml-auto" style={{ color: stateMap[r.status]?.color }}>{stateMap[r.status]?.label || r.status}</Badge>
                <span className="text-[10px] text-slate-400 font-mono">{formatDateES(r.updated_at)}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════
// REQUEST DOCUMENTS SECTION (unified hr_documents)
// ═══════════════════════════════════════════
function RequestDocumentsSection({ axiosAuth, requestId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewType, setPreviewType] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosAuth().get(`/hr/documents/by-request/${requestId}`);
      setDocs(res.data || []);
    } catch {
      // ignore — fetch failed silently
    }
    finally { setLoading(false); }
  }, [axiosAuth, requestId]);

  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Selecciona un archivo"); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axiosAuth().post(`/vacation-requests/${requestId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento subido");
      fileRef.current.value = "";
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al subir"); }
  };

  const download = async (doc) => {
    try {
      const res = await axiosAuth().get(`/hr/documents/${doc.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = doc.filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Error al descargar"); }
  };

  const preview = async (doc) => {
    try {
      const res = await axiosAuth().get(`/hr/documents/${doc.id}/preview`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: doc.content_type }));
      setPreviewUrl(url);
      setPreviewType(doc.content_type);
    } catch { toast.error("Vista previa no disponible"); }
  };

  const STATE_COLORS = { pendiente: "text-amber-600 border-amber-200", recibida: "text-blue-600 border-blue-200", validada: "text-green-600 border-green-200", rechazada: "text-red-600 border-red-200" };
  const STATE_LABELS = { pendiente: "Pendiente", recibida: "Recibida", validada: "Validada", rechazada: "Rechazada" };

  return (
    <div data-testid="request-documents-section">
      <span className="text-slate-500 font-semibold">Documentos ({docs.length})</span>
      {loading ? <p className="text-[10px] text-slate-400 mt-1">Cargando...</p> : (
        <>
          {docs.length > 0 && (
            <div className="mt-1 space-y-1.5">
              {docs.map(d => {
                const previewable = ["application/pdf", "image/jpeg", "image/png"].includes(d.content_type);
                return (
                  <div key={d.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded-sm border border-slate-100" data-testid={`req-doc-${d.id}`}>
                    <FileText className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">{d.filename}</p>
                      <p className="text-[9px] text-slate-400">{d.category_label || d.category} · {d.uploaded_by} · {formatDateES(d.created_at)}</p>
                    </div>
                    <Badge variant="outline" className={`text-[8px] ${STATE_COLORS[d.state] || ""}`}>{STATE_LABELS[d.state] || d.state}</Badge>
                    {previewable && (
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => preview(d)} title="Vista previa"><Eye className="w-3 h-3" /></Button>
                    )}
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => download(d)} title="Descargar"><Download className="w-3 h-3" /></Button>
                  </div>
                );
              })}
            </div>
          )}
          <div className="flex gap-2 mt-2">
            <Input type="file" ref={fileRef} accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx" className="h-8 text-[10px] rounded-sm flex-1 border-slate-200" data-testid="req-doc-upload" />
            <Button variant="outline" size="sm" className="h-8 text-[10px] rounded-sm" onClick={upload} data-testid="btn-req-upload-doc">
              <Upload className="w-3 h-3 mr-1" /> Subir
            </Button>
          </div>
        </>
      )}

      {/* Preview Dialog */}
      {previewUrl && (
        <Dialog open={!!previewUrl} onOpenChange={() => { if (previewUrl) window.URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}>
          <DialogContent className="max-w-3xl max-h-[80vh]" data-testid="preview-dialog">
            <DialogHeader><DialogTitle className="text-sm uppercase tracking-wider">Vista previa</DialogTitle></DialogHeader>
            {previewType === "application/pdf" ? (
              <iframe src={previewUrl} className="w-full h-[60vh] rounded-sm" title="Preview" />
            ) : (
              <img src={previewUrl} alt="Preview" className="max-w-full max-h-[60vh] object-contain mx-auto rounded-sm" />
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════
// REQUESTS TAB
// ═══════════════════════════════════════════
// ═══════════════════════════════════════════
// Iter 188 (P1) — PendientesMiasTab
// ═══════════════════════════════════════════
function PendientesMiasTab({ axiosAuth, user, onAfterAction }) {
  const [data, setData] = useState({ as_direct_manager: [], as_general_manager: [], totals: { direct: 0, manager: 0 }, is_admin: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actingOn, setActingOn] = useState(null);
  // Iter 188 (P1 fix) — notas indexadas por id para evitar fuga entre cards
  const [notesById, setNotesById] = useState({});

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await axiosAuth().get("/vacation-requests/pending-for-me");
      setData(res.data || { as_direct_manager: [], as_general_manager: [], totals: { direct: 0, manager: 0 } });
    } catch (e) {
      setError(e.response?.data?.detail || "No se pudo cargar la lista de pendientes.");
    } finally {
      setLoading(false);
    }
  }, [axiosAuth]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const act = async (req, action) => {
    const notes = (notesById[req.id] || "").trim();
    if (action === "reject" && !notes) {
      toast.warning("Añade un motivo para rechazar (obligatorio).");
      return;
    }
    const verb = action === "approve" ? "aprobar" : "rechazar";
    if (!window.confirm(`¿Confirmas ${verb} la solicitud de ${req.employee_name || "(empleado)"} (${req.start_date} → ${req.end_date})?`)) return;
    try {
      await axiosAuth().put(`/vacation-requests/${req.id}/approve`, { action, notes });
      toast.success(action === "approve" ? "Aprobada" : "Rechazada");
      setActingOn(null);
      setNotesById((m) => { const n = { ...m }; delete n[req.id]; return n; });
      await fetchData();
      onAfterAction?.();
      try { window.dispatchEvent(new Event("pending-approval-changed")); } catch {/* noop */}
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const renderCard = (r, section) => {
    const isActing = actingOn === r.id;
    return (
      <Card key={r.id} className="rounded-sm border-slate-200" data-testid={`pendientes-card-${r.id}`}>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <h3 className="font-semibold text-slate-900 text-sm">{r.employee_name || "(sin nombre)"}</h3>
              <p className="text-[11px] text-slate-500">
                {r.center || "(sin centro)"} · {r.department || "(sin departamento)"}
              </p>
            </div>
            <Badge className={
              r.status === "pendiente_jefe" ? "bg-amber-100 text-amber-700 border border-amber-200 uppercase text-[10px] tracking-wider" :
              "bg-amber-200 text-amber-900 border border-amber-300 uppercase text-[10px] tracking-wider"
            }>
              {r.status === "pendiente_jefe" ? "Pendiente jefe directo" : "Pendiente gerente"}
            </Badge>
          </div>

          <div className="grid grid-cols-2 gap-2 text-[12px]">
            <div>
              <span className="text-slate-500 font-semibold uppercase tracking-wider text-[10px]">Tipo</span>
              <p className="text-slate-800">{r.request_type}</p>
            </div>
            <div>
              <span className="text-slate-500 font-semibold uppercase tracking-wider text-[10px]">Días</span>
              <p className="text-slate-800 font-semibold">{r.working_days || r.calculated_days || r.days_count || 0}</p>
            </div>
            <div className="col-span-2">
              <span className="text-slate-500 font-semibold uppercase tracking-wider text-[10px]">Fechas</span>
              <p className="text-slate-800">{formatDateES(r.start_date)} → {formatDateES(r.end_date)}</p>
            </div>
            {r.comments && (
              <div className="col-span-2">
                <span className="text-slate-500 font-semibold uppercase tracking-wider text-[10px]">Comentario del empleado</span>
                <p className="text-slate-700 italic text-[11px] bg-slate-50 p-2 rounded-sm border border-slate-100">{r.comments}</p>
              </div>
            )}
          </div>

          <SimpleFlowProgress req={r} />

          {!isActing ? (
            <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
              <Button
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white text-xs rounded-sm"
                onClick={() => setActingOn(r.id)}
                data-testid={`pendientes-act-${r.id}`}
              >
                <Check className="w-3 h-3 mr-1" />
                {section === "direct" ? "Aprobar como jefe" : "Aprobar como gerente"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-xs rounded-sm"
                onClick={() => setActingOn(r.id)}
                data-testid={`pendientes-reject-${r.id}`}
              >
                Rechazar / añadir nota
              </Button>
            </div>
          ) : (
            <div className="pt-2 border-t border-slate-100 space-y-2">
              <Textarea
                value={notesById[r.id] || ""}
                onChange={(e) => setNotesById((m) => ({ ...m, [r.id]: e.target.value }))}
                placeholder="Notas / motivo (obligatorio para rechazo)…"
                className="text-xs rounded-sm border-slate-300 min-h-[60px]"
                data-testid={`pendientes-notes-${r.id}`}
              />
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white text-xs rounded-sm"
                  onClick={() => act(r, "approve")}
                  data-testid={`pendientes-confirm-approve-${r.id}`}
                >
                  <Check className="w-3 h-3 mr-1" />
                  Confirmar aprobación
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  className="text-xs rounded-sm"
                  onClick={() => act(r, "reject")}
                  data-testid={`pendientes-confirm-reject-${r.id}`}
                >
                  <X className="w-3 h-3 mr-1" />
                  Confirmar rechazo
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-xs"
                  onClick={() => { setActingOn(null); setNotesById((m) => { const n = { ...m }; delete n[r.id]; return n; }); }}
                  data-testid={`pendientes-cancel-${r.id}`}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const directList = data.as_direct_manager || [];
  const managerList = data.as_general_manager || [];
  const empty = directList.length === 0 && managerList.length === 0;

  return (
    <div className="space-y-6" data-testid="pendientes-mias-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-900">Pendientes mías</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Solicitudes esperando tu decisión como {data.is_admin ? "admin / super_admin (ves todo)" : "jefe directo o gerente"}.
          </p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm" className="rounded-sm text-xs" data-testid="pendientes-refresh-btn">
          <Clock className="w-3 h-3 mr-1" /> {loading ? "Cargando…" : "Actualizar"}
        </Button>
      </div>

      {error && (
        <Card className="rounded-sm border-red-200 bg-red-50">
          <CardContent className="p-3 text-xs text-red-700">{error}</CardContent>
        </Card>
      )}

      {!loading && empty && !error && (
        <Card className="rounded-sm border-emerald-200 bg-emerald-50" data-testid="pendientes-empty">
          <CardContent className="p-6 text-center">
            <Check className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
            <p className="text-sm font-semibold text-emerald-800">¡Nada pendiente!</p>
            <p className="text-xs text-emerald-700 mt-1">No tienes solicitudes esperando tu decisión.</p>
          </CardContent>
        </Card>
      )}

      {/* Sección: Como jefe directo */}
      <div className="space-y-3" data-testid="pendientes-section-direct">
        <div className="flex items-center gap-2">
          <UserCheck className="w-4 h-4 text-blue-600" />
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">Como jefe directo</h3>
          <Badge variant="outline" className="text-[10px] rounded-sm">{directList.length}</Badge>
        </div>
        {directList.length === 0 && !loading && (
          <p className="text-[11px] text-slate-400 italic px-2">Sin solicitudes de tu equipo esperando.</p>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {directList.map((r) => renderCard(r, "direct"))}
        </div>
      </div>

      {/* Sección: Como gerente */}
      <div className="space-y-3" data-testid="pendientes-section-manager">
        <div className="flex items-center gap-2">
          <UserCog className="w-4 h-4 text-purple-600" />
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">Como gerente</h3>
          <Badge variant="outline" className="text-[10px] rounded-sm">{managerList.length}</Badge>
        </div>
        {managerList.length === 0 && !loading && (
          <p className="text-[11px] text-slate-400 italic px-2">Sin solicitudes esperando tu aprobación final.</p>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {managerList.map((r) => renderCard(r, "manager"))}
        </div>
      </div>
    </div>
  );
}



function RequestsTab({ axiosAuth, hrConfig, departments = [], centers = [], initialFilter }) {
  const { years: availableYears } = useYearRange();
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [totalRequests, setTotalRequests] = useState(0);  // Iter 198
  const [employeesList, setEmployeesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(() => {
    const base = { status: "all", request_type: "all", center: "all", department: "all", employee: "all", month: "", search: "", active_only: false, date_from: "", date_to: "", year: "all", quick: "all" };
    // Iter 188 (P0) — nuevos estados del flujo simplificado
    if (initialFilter === "pendientes") return { ...base, status: "pendiente_jefe,pendiente_gerente,en_aprobacion" };
    if (initialFilter === "bajas_activas") return { ...base, request_type: "baja_medica", status: "comunicada,registrada", active_only: true };
    return base;
  });
  const [selected, setSelected] = useState(null);
  const [reviewNotes, setReviewNotes] = useState("");
  // Iter 209 — Modal de creación administrativa
  const [adminCreateOpen, setAdminCreateOpen] = useState(false);
  const canAdminCreate = user?.role === "super_admin" || user?.role === "admin";

  const stateMap = {};
  (hrConfig?.leave_states || []).forEach(s => { stateMap[s.value] = s; });
  const typeMap = {};
  (hrConfig?.request_types || []).forEach(t => { typeMap[t.value] = t; });

  // Iter 188 (P1) — Cargar lista de empleados para el filtro dropdown
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await axiosAuth().get("/employees", { params: { active: "true" } });
        if (!cancel) setEmployeesList(Array.isArray(res.data) ? res.data : (res.data?.items || []));
      } catch {
        // silent — fallback to no employee filter
      }
    })();
    return () => { cancel = true; };
  }, [axiosAuth]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status !== "all") params.status = filters.status;
      if (filters.request_type !== "all") params.request_type = filters.request_type;
      if (filters.center !== "all") params.center = filters.center;
      if (filters.department && filters.department !== "all") params.department = filters.department;
      if (filters.employee && filters.employee !== "all") params.employee_id = filters.employee;
      if (filters.month) params.month = filters.month;
      if (filters.active_only) params.active_only = "true";
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.year && filters.year !== "all") params.year = filters.year;
      if (filters.search) params.search = filters.search;
      const res = await axiosAuth().get("/vacation-requests", { params });
      const items = Array.isArray(res.data) ? res.data : (res.data?.items || []);
      setRequests(items);
      setTotalRequests(Array.isArray(res.data) ? items.length : (res.data?.total || items.length));
    } catch { toast.error("Error al cargar solicitudes"); }
    finally { setLoading(false); }
  }, [axiosAuth, filters]);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (id, action, notes) => {
    try {
      await axiosAuth().put(`/vacation-requests/${id}/approve`, { action, notes: notes || reviewNotes });
      toast.success(action === "approve" ? "Aprobado" : action === "reject" ? "Rechazado" : "Procesado");
      setSelected(null);
      setReviewNotes("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const exportExcel = async () => {
    try {
      const params = {};
      if (filters.month) params.month = filters.month;
      if (filters.center !== "all") params.center = filters.center;
      if (filters.request_type !== "all") params.request_type = filters.request_type;
      if (filters.status !== "all") params.status = filters.status;
      const res = await axiosAuth().get("/hr/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url;
      a.download = `rrhh_${filters.month || "todos"}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel descargado");
    } catch { toast.error("Error al exportar"); }
  };

  // Iter 188 (P0) — Permiso de aprobación basado en NUEVO status, no en approval_flow
  // - pendiente_jefe → jefe directo del empleado o admin
  // - pendiente_gerente → gerente configurado o super_admin
  // El backend valida la identidad real. Aquí solo decidimos si mostrar botones.
  const canApproveRequest = (req) => {
    if (!req) return false;
    const role = user?.role;
    if (["admin", "super_admin"].includes(role)) return true;
    if (req.status === "pendiente_jefe") {
      // Cualquier user con role manager-ish; el backend valida si es realmente
      // el jefe directo del empleado.
      return ["jefe_taller", "jefe_recambios", "jefe_directo"].includes(role);
    }
    if (req.status === "pendiente_gerente") {
      // El gerente configurado por email se identifica en backend.
      return role === "gerente";
    }
    return false;
  };

  const getCurrentStepLabel = (req) => {
    const map = {
      pendiente_jefe: "Pendiente: Jefe directo",
      pendiente_gerente: "Pendiente: Gerente",
      aprobada: "Aprobada",
      rechazada: "Rechazada",
      cancelada_empleado: "Cancelada por empleado",
      comunicada: "Comunicada",
      documentacion_pendiente: "Documentación pendiente",
      en_aprobacion: "Pendiente",
    };
    return map[req?.status] || req?.status || "";
  };

  return (
    <div className="space-y-4" data-testid="hr-requests-tab">
      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-end">
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Periodo</Label>
          <div className="flex gap-1">
            <Select value={filters.month ? filters.month.split("-")[1] : "all"} onValueChange={v => {
              if (v === "all") { setFilters({ ...filters, month: "" }); return; }
              const yr = filters.month ? filters.month.split("-")[0] : new Date().getFullYear().toString();
              setFilters({ ...filters, month: `${yr}-${v}` });
            }}>
              <SelectTrigger className="h-8 text-xs rounded-sm w-28" data-testid="filter-month"><SelectValue placeholder="Mes" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"].map((m, i) => (
                  <SelectItem key={i} value={String(i + 1).padStart(2, "0")}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.month ? filters.month.split("-")[0] : new Date().getFullYear().toString()} onValueChange={v => {
              const mon = filters.month ? filters.month.split("-")[1] : String(new Date().getMonth() + 1).padStart(2, "0");
              if (!filters.month || filters.month.split("-")[1] === undefined) {
                setFilters({ ...filters, month: `${v}-${mon}` });
              } else {
                setFilters({ ...filters, month: `${v}-${mon}` });
              }
            }}>
              <SelectTrigger className="h-8 text-xs rounded-sm w-20" data-testid="filter-year"><SelectValue /></SelectTrigger>
              <SelectContent>
                {availableYears.map(yr => (
                  <SelectItem key={yr} value={yr.toString()}>{yr}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Estado</Label>
          <Select value={filters.status} onValueChange={v => setFilters({ ...filters, status: v })}>
            <SelectTrigger className="h-8 text-xs rounded-sm w-44" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {/* Iter 188 (P1 fix) — Primero estados nuevos del flujo simple */}
              {(hrConfig?.leave_states || [])
                .filter(s => ["pendiente_jefe", "pendiente_gerente", "aprobada", "rechazada",
                              "cancelada_empleado", "comunicada", "documentacion_pendiente"].includes(s.value))
                .map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              {/* Legacy: solo si todavía hay documentos con esos estados (tras migración, casi siempre 0) */}
              {(hrConfig?.leave_states || [])
                .filter(s => !["pendiente_jefe", "pendiente_gerente", "aprobada", "rechazada",
                               "cancelada_empleado", "comunicada", "documentacion_pendiente"].includes(s.value))
                .map(s => <SelectItem key={s.value} value={s.value} className="text-slate-400 text-[11px]">{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Tipo</Label>
          <Select value={filters.request_type} onValueChange={v => setFilters({ ...filters, request_type: v })}>
            <SelectTrigger className="h-8 text-xs rounded-sm w-36" data-testid="filter-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {(hrConfig?.request_types || []).filter(t => t.active).map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {/* Iter 188 (P1) — Filtros dropdown reales: Centro, Departamento, Empleado */}
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Centro</Label>
          <Select value={filters.center} onValueChange={v => setFilters({ ...filters, center: v })}>
            <SelectTrigger className="h-8 text-xs rounded-sm w-40" data-testid="filter-center"><SelectValue placeholder="Todos" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {(centers || []).map(c => <SelectItem key={c.id || c.value || c.name} value={c.value || c.name}>{c.label || c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Departamento</Label>
          <Select value={filters.department || "all"} onValueChange={v => setFilters({ ...filters, department: v })}>
            <SelectTrigger className="h-8 text-xs rounded-sm w-44" data-testid="filter-department"><SelectValue placeholder="Todos" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {(departments || []).map(d => <SelectItem key={d.id || d.value || d.name} value={d.value || d.name}>{d.label || d.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Empleado</Label>
          <Select value={filters.employee || "all"} onValueChange={v => setFilters({ ...filters, employee: v })}>
            <SelectTrigger className="h-8 text-xs rounded-sm w-48" data-testid="filter-employee"><SelectValue placeholder="Todos" /></SelectTrigger>
            <SelectContent className="max-h-64 overflow-y-auto">
              <SelectItem value="all">Todos</SelectItem>
              {employeesList.map(e => <SelectItem key={e.id} value={e.id}>{e.name || e.full_name || e.email}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Buscar (nombre/notas)</Label>
          <Input
            value={filters.search || ""}
            onChange={e => setFilters({ ...filters, search: e.target.value })}
            onKeyDown={e => e.key === "Enter" && load()}
            placeholder="texto libre…"
            className="h-8 text-xs rounded-sm w-44"
            data-testid="filter-search"
          />
        </div>
        <Button variant="outline" size="sm" className="h-8 text-xs rounded-sm" onClick={load} data-testid="btn-refresh"><Filter className="w-3 h-3 mr-1" /> Filtrar</Button>
        <Button variant="outline" size="sm" className="h-8 text-xs rounded-sm" onClick={() => { setFilters({ status: "all", request_type: "all", center: "all", department: "all", employee: "all", search: "", date_from: "", date_to: "", year: "all", quick: "all" }); setTimeout(() => load(), 50); }} data-testid="btn-clear-filters">Limpiar filtros</Button>
        {canAdminCreate && (
          <Button
            size="sm"
            className="h-8 text-xs rounded-sm bg-slate-900 hover:bg-slate-800 text-white"
            onClick={() => setAdminCreateOpen(true)}
            data-testid="btn-admin-create-request"
          >
            <Plus className="w-3 h-3 mr-1" /> Nueva solicitud por empleado
          </Button>
        )}
        <Button size="sm" className="h-8 text-xs rounded-sm bg-green-700 hover:bg-green-800 text-white ml-auto" onClick={exportExcel} data-testid="btn-export-excel"><FileDown className="w-3 h-3 mr-1" /> Exportar</Button>
      </div>

      {/* Iter 198 — Chips de acceso rápido */}
      <div className="flex flex-wrap items-center gap-1.5" data-testid="quick-chips">
        {[
          { value: "all", label: "Todas" },
          { value: "pending", label: "Pendientes", statuses: ["pendiente_jefe", "pendiente_admin", "en_aprobacion"] },
          { value: "approved", label: "Aprobadas", statuses: ["aprobada"] },
          { value: "rejected", label: "Rechazadas", statuses: ["rechazada"] },
          { value: "cancelled", label: "Canceladas", statuses: ["cancelada_empleado", "cancelada_admin"] },
          { value: "medical", label: "Bajas médicas", request_type: "baja_medica" },
          { value: "admin_edited", label: "Corregidas admin" },
        ].map((c) => (
          <button
            key={c.value}
            onClick={() => {
              const next = { ...filters, quick: c.value };
              if (c.statuses) next.status = c.statuses.join(",");
              else if (c.value === "all") next.status = "all";
              if (c.request_type) next.request_type = c.request_type;
              setFilters(next);
              setTimeout(() => load(), 50);
            }}
            className={`text-[11px] px-2.5 py-1 rounded-sm border ${(filters.quick || "all") === c.value ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"}`}
            data-testid={`chip-${c.value}`}
          >{c.label}</button>
        ))}
        <span className="ml-auto text-[11px] text-slate-500" data-testid="results-counter">
          Mostrando {requests.length}{typeof totalRequests === "number" && totalRequests !== requests.length ? ` de ${totalRequests}` : ""} solicitudes
        </span>
      </div>

      {/* Table */}
      <Card className="border-slate-200 rounded-sm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-[10px] uppercase">Empleado</TableHead>
                  <TableHead className="text-[10px] uppercase">Tipo</TableHead>
                  <TableHead className="text-[10px] uppercase">Fechas</TableHead>
                  <TableHead className="text-[10px] uppercase text-center">Dias</TableHead>
                  <TableHead className="text-[10px] uppercase">Estado</TableHead>
                  <TableHead className="text-[10px] uppercase">Paso actual</TableHead>
                  <TableHead className="text-[10px] uppercase text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center text-xs text-slate-400 py-8">Cargando...</TableCell></TableRow>
                ) : requests.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center text-xs text-slate-400 py-8">Sin solicitudes</TableCell></TableRow>
                ) : requests.map(r => {
                  const st = stateMap[r.status] || { label: r.status, color: "#94a3b8" };
                  const tp = typeMap[r.request_type] || { label: r.request_type };
                  const approvalPending = canApproveRequest(r);
                  // Iter 218 — Mostrar modo de cálculo junto al nº de días descontados.
                  const cmode = r.calculation_mode;
                  const dCount = r.working_days;
                  let dLabel;
                  if (cmode === "business_days") dLabel = `${dCount} laborables`;
                  else if (cmode === "natural_days") dLabel = `${dCount} naturales`;
                  else if (cmode === "manual") dLabel = `Manual: ${dCount}`;
                  else dLabel = `${dCount}`;
                  const naturalP = r.natural_days_in_period;
                  const dTitle = naturalP != null && naturalP !== dCount
                    ? `Días descontados: ${dCount}${cmode ? ` (${cmode})` : ""} · Periodo natural: ${naturalP}`
                    : `Días descontados: ${dCount}${cmode ? ` (${cmode})` : ""}`;
                  return (
                    <TableRow key={r.id} className={`cursor-pointer ${approvalPending ? "bg-amber-50/50 hover:bg-amber-50" : "hover:bg-slate-50"}`} onClick={() => { setSelected(r); setReviewNotes(r.review_notes || ""); }}>
                      <TableCell className="text-xs font-medium">{r.employee_name}<span className="text-slate-400 block text-[10px]">{r.center}</span></TableCell>
                      <TableCell className="text-xs">{tp.label}</TableCell>
                      <TableCell className="text-xs font-mono">{formatDateES(r.start_date)} → {formatDateES(r.end_date)}</TableCell>
                      <TableCell
                        className="text-xs text-center font-mono font-bold whitespace-nowrap"
                        title={dTitle}
                        data-testid={`hr-row-days-${r.id}`}
                      >
                        {dLabel}
                      </TableCell>
                      <TableCell><Badge variant="outline" className="text-[10px] border" style={{ color: st.color, borderColor: st.color }}>{st.label}</Badge></TableCell>
                      <TableCell className="text-[10px] text-slate-500">{getCurrentStepLabel(r)}</TableCell>
                      <TableCell className="text-right">
                        {approvalPending && <Badge className="bg-amber-100 text-amber-700 text-[9px] border-amber-300">Tu turno</Badge>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-sm uppercase tracking-wider">Detalle de Solicitud</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-slate-500">Empleado:</span> <strong>{selected.employee_name}</strong></div>
                <div><span className="text-slate-500">Centro:</span> {selected.center || "-"}</div>
                <div><span className="text-slate-500">Departamento:</span> {selected.department || "-"}</div>
                <div><span className="text-slate-500">Tipo:</span> {typeMap[selected.request_type]?.label || selected.request_type}</div>
                <div><span className="text-slate-500">Fechas:</span> <span className="font-mono">{formatDateES(selected.start_date)} → {formatDateES(selected.end_date)}</span></div>
                <div><span className="text-slate-500">Dias lab.:</span> <strong className="text-lg">{selected.working_days}</strong></div>
                <div><span className="text-slate-500">Estado:</span> <Badge variant="outline" className="text-[10px]" style={{ color: stateMap[selected.status]?.color }}>{stateMap[selected.status]?.label || selected.status}</Badge></div>
                <div><span className="text-slate-500">Creada:</span> {formatDateES(selected.created_at)}</div>
              </div>

              {selected.comments && <div><span className="text-slate-500">Comentarios:</span><p className="mt-1 p-2 bg-slate-50 rounded-sm">{selected.comments}</p></div>}

              {/* Communication-type notice (non-approvable) */}
              {selected.requires_approval === false && (
                <div className="p-3 bg-purple-50 border border-purple-200 rounded-sm" data-testid="communicated-type-notice">
                  <div className="flex items-center gap-2 mb-1">
                    <Info className="w-3.5 h-3.5 text-purple-500" />
                    <span className="text-[10px] uppercase font-bold text-purple-700 tracking-wider">Tipo comunicado</span>
                  </div>
                  <p className="text-[11px] text-purple-600">
                    Este tipo de solicitud no requiere flujo de aprobacion. Se gestiona como comunicacion directa con circuito documental.
                  </p>
                </div>
              )}

              {/* Iter 188 (P0) — Progreso del flujo simplificado (2 pasos) */}
              {selected.status && !["comunicada", "documentacion_pendiente", "registrada"].includes(selected.status) && (
                <div data-testid="simple-flow-progress">
                  <span className="text-slate-500 font-semibold block mb-2">Progreso de la solicitud:</span>
                  <SimpleFlowProgress req={selected} />
                </div>
              )}

              {/* Historial detallado (approval_history) */}
              {selected.approval_history && selected.approval_history.length > 0 && (
                <div data-testid="approval-history-list">
                  <span className="text-slate-500 font-semibold block mb-2">Historial:</span>
                  <div className="space-y-1.5">
                    {selected.approval_history.map((h, i) => (
                      <div key={i} className="flex items-start gap-2 p-2 rounded-sm bg-slate-50 text-[11px]">
                        <span className="inline-block px-1.5 py-0.5 rounded-sm bg-slate-200 font-mono text-[10px] flex-shrink-0">
                          {h.action}
                        </span>
                        <div className="flex-1">
                          <div className="text-slate-700">
                            <strong>{h.by_user_name || h.by_user_id || "—"}</strong>
                            <span className="text-slate-400"> · {h.by_user_role || ""}</span>
                            <span className="text-slate-400"> · {h.at ? formatDateES(h.at) : ""}</span>
                          </div>
                          {h.notes && <p className="italic text-slate-600 mt-0.5">{h.notes}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Legacy approval_flow timeline (solo si NO hay approval_history) */}
              {(!selected.approval_history || selected.approval_history.length === 0) && selected.requires_approval !== false && selected.approval_flow && selected.approval_flow.length > 0 && (
                <div data-testid="approval-flow-timeline">
                  <span className="text-slate-500 font-semibold block mb-2">Flujo de aprobacion:</span>
                  <div className="space-y-2">
                    {selected.approval_flow.map((s, i) => (
                      <div key={i} className={`flex items-center gap-3 p-2.5 rounded-sm border ${s.status === "approved" ? "bg-green-50 border-green-200" : s.status === "rejected" ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"}`}>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${s.status === "approved" ? "bg-green-500" : s.status === "rejected" ? "bg-red-500" : "bg-amber-400"}`}>
                          {s.status === "approved" ? <Check className="w-3.5 h-3.5 text-white" /> : s.status === "rejected" ? <X className="w-3.5 h-3.5 text-white" /> : <Clock className="w-3.5 h-3.5 text-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold">{s.label}</p>
                          {s.resolved_by_name && <p className="text-[10px] text-slate-500">{s.resolved_by_name} - {formatDateES(s.resolved_at)}</p>}
                          {s.notes && <p className="text-[10px] text-slate-600 mt-0.5 italic">{s.notes}</p>}
                        </div>
                        <Badge variant="outline" className="text-[9px]" style={{ color: s.status === "approved" ? "#16a34a" : s.status === "rejected" ? "#dc2626" : "#d97706" }}>
                          {s.status === "approved" ? "Aprobado" : s.status === "rejected" ? "Rechazado" : "Pendiente"}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Overlap Warnings */}
              {selected.overlap_warnings && selected.overlap_warnings.length > 0 && (
                <div className="p-2.5 bg-orange-50 border border-orange-200 rounded-sm">
                  <span className="text-orange-700 font-semibold text-[10px] uppercase">Avisos de solape:</span>
                  {selected.overlap_warnings.map((w, i) => (
                    <p key={i} className="text-[11px] text-orange-600 mt-1">{w.message}</p>
                  ))}
                </div>
              )}

              {/* Documents from unified hr_documents */}
              <RequestDocumentsSection axiosAuth={axiosAuth} requestId={selected.id} />

              {/* Review notes */}
              {(canApproveRequest(selected) || (selected.requires_approval === false && ["comunicada", "documentacion_pendiente"].includes(selected.status) && ["admin", "super_admin"].includes(user?.role))) && (
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Observaciones</Label>
                  <Textarea value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} className="text-xs rounded-sm h-16" data-testid="review-notes" />
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex-wrap gap-2">
            {selected && canApproveRequest(selected) && (
              <>
                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white text-xs rounded-sm" onClick={() => handleApprove(selected.id, "approve", reviewNotes)} data-testid="btn-approve">
                  <Check className="w-3 h-3 mr-1" /> {selected.status === "pendiente_jefe" ? "Aprobar como jefe" : selected.status === "pendiente_gerente" ? "Aprobar como gerente" : "Aprobar"}
                </Button>
                <Button size="sm" variant="destructive" className="text-xs rounded-sm" onClick={() => handleApprove(selected.id, "reject", reviewNotes)} data-testid="btn-reject">
                  <X className="w-3 h-3 mr-1" /> Rechazar
                </Button>
              </>
            )}
            {/* Actions for communicated (non-approvable) types */}
            {selected?.requires_approval === false && selected?.status === "comunicada" && ["admin", "super_admin"].includes(user?.role) && (
              <>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-sm" onClick={() => handleApprove(selected.id, "register", reviewNotes)} data-testid="btn-register">
                  <Check className="w-3 h-3 mr-1" /> Registrar
                </Button>
                <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white text-xs rounded-sm" onClick={() => handleApprove(selected.id, "doc_pending", reviewNotes)} data-testid="btn-doc-pending">
                  Doc. Pendiente
                </Button>
                {/* Iter 188 (P0) — "Cerrar" renombrado a "Archivar", solo admin/super_admin */}
                <Button size="sm" variant="outline" className="text-xs rounded-sm" onClick={() => handleApprove(selected.id, "archive", "")} data-testid="btn-archive">Archivar</Button>
              </>
            )}
            {selected?.requires_approval === false && selected?.status === "documentacion_pendiente" && ["admin", "super_admin"].includes(user?.role) && (
              <>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-sm" onClick={() => handleApprove(selected.id, "register", reviewNotes)} data-testid="btn-register-from-doc">
                  <Check className="w-3 h-3 mr-1" /> Registrar
                </Button>
                <Button size="sm" variant="outline" className="text-xs rounded-sm" onClick={() => handleApprove(selected.id, "archive", "")} data-testid="btn-archive-doc">Archivar</Button>
              </>
            )}
            {selected?.requires_approval === false && selected?.status === "registrada" && ["admin", "super_admin"].includes(user?.role) && (
              <Button size="sm" variant="outline" className="text-xs rounded-sm" onClick={() => handleApprove(selected.id, "archive", "")} data-testid="btn-archive-registered">Archivar</Button>
            )}
            {/* Standard status-based actions for approvable types */}
            {selected?.requires_approval !== false && selected?.status === "comunicada" && ["admin", "super_admin"].includes(user?.role) && (
              <>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-sm" onClick={() => handleApprove(selected.id, "register", "")} data-testid="btn-register">
                  <Check className="w-3 h-3 mr-1" /> Registrar
                </Button>
                <Button size="sm" variant="outline" className="text-xs rounded-sm" onClick={() => handleApprove(selected.id, "archive", "")} data-testid="btn-archive">Archivar</Button>
              </>
            )}
            {/* Iter 188 (P0) — "Archivar" para solicitudes ya aprobadas (sin cambio de status) */}
            {selected?.status === "aprobada" && ["admin", "super_admin"].includes(user?.role) && !selected?.archived && (
              <Button size="sm" variant="outline" className="text-xs rounded-sm" onClick={() => handleApprove(selected.id, "archive", "")} data-testid="btn-archive-approved">Archivar</Button>
            )}
            {/* Iter 197 — Acciones admin: Editar / Cancelar / Eliminar */}
            {selected && ["admin", "super_admin"].includes(user?.role) && (
              <AdminVacationActions
                axiosAuth={axiosAuth}
                req={selected}
                isSuperAdmin={user?.role === "super_admin"}
                onChanged={async () => {
                  try {
                    const r = await axiosAuth().get("/vacation-requests");
                    setRequests(r.data);
                    const updated = (r.data || []).find((x) => x.id === selected.id);
                    if (updated) setSelected(updated); else setSelected(null);
                  } catch { /* silencioso */ }
                }}
              />
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 209 — Modal de creación administrativa de solicitudes */}
      <AdminCreateRequestModal
        open={adminCreateOpen}
        onOpenChange={setAdminCreateOpen}
        axiosAuth={axiosAuth}
        onCreated={() => { setAdminCreateOpen(false); load(); }}
      />
    </div>
  );
}

// ═══════════════════════════════════════════
// Iter 188 (P0) — SimpleFlowProgress: línea de progreso 2 pasos
// ═══════════════════════════════════════════
const stepClass = (state) => {
  if (state === "done") return "bg-emerald-500 text-white border-emerald-600";
  if (state === "rejected") return "bg-red-500 text-white border-red-600";
  if (state === "active") return "bg-amber-400 text-white border-amber-500 animate-pulse";
  return "bg-slate-200 text-slate-500 border-slate-300";
};

function FlowStep({ idx, label, state, testId }) {
  return (
    <div className="flex flex-col items-center" data-testid={testId}>
      <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center text-[11px] font-bold ${stepClass(state)}`}>
        {state === "done" ? "✓" : state === "rejected" ? "✗" : idx}
      </div>
      <span className="text-[10px] text-slate-600 mt-1 whitespace-nowrap">{label}</span>
    </div>
  );
}
function FlowConn({ done }) {
  return <div className={`flex-1 h-0.5 ${done ? "bg-emerald-400" : "bg-slate-200"}`} style={{ minWidth: 20 }} />;
}

function SimpleFlowProgress({ req }) {
  if (!req) return null;
  const status = req.status;
  const hist = req.approval_history || [];
  const directDone = hist.some(h => ["approved_by_direct", "rejected_by_direct"].includes(h.action));
  const directRejected = hist.some(h => h.action === "rejected_by_direct") || (status === "rechazada" && req.rejected_by === "rejected_by_direct_manager");
  const managerDone = hist.some(h => ["approved_by_manager", "rejected_by_manager"].includes(h.action)) || status === "aprobada";
  const managerRejected = hist.some(h => h.action === "rejected_by_manager") || (status === "rechazada" && req.rejected_by === "rejected_by_general_manager");
  const cancelled = status === "cancelada_empleado";

  const s1 = directRejected ? "rejected" : (directDone ? "done" : (status === "pendiente_jefe" ? "active" : "idle"));
  const s2 = managerRejected ? "rejected" : (managerDone ? "done" : (status === "pendiente_gerente" ? "active" : "idle"));
  const s3 = status === "aprobada" ? "done" : "idle";

  return (
    <div className="flex items-center gap-2" data-testid="flow-progress-bar">
      <FlowStep idx={1} label="Solicitud" state="done" />
      <FlowConn done />
      <FlowStep idx={2} label="Jefe directo" state={s1} testId="flow-step-direct" />
      <FlowConn done={s2 !== "idle"} />
      <FlowStep idx={3} label="Gerente" state={s2} testId="flow-step-manager" />
      <FlowConn done={s3 === "done"} />
      <FlowStep idx={4} label={cancelled ? "Cancelada" : "Aprobada"} state={cancelled ? "rejected" : s3} testId="flow-step-final" />
    </div>
  );
}



// ═══════════════════════════════════════════
// APPROVAL MATRIX TAB
// ═══════════════════════════════════════════
const APPROVER_TYPE_OPTIONS = [
  { value: "jefe_directo", label: "Jefe directo del empleado", needs_value: false },
  { value: "gerencia_centro", label: "Gerencia del centro", needs_value: false },
  { value: "admin_rrhh", label: "Admin / RRHH", needs_value: false },
  { value: "role", label: "Rol", needs_value: "role" },
  { value: "user", label: "Usuario concreto", needs_value: "user" },
];
const APPROVER_TYPE_LABEL = Object.fromEntries(APPROVER_TYPE_OPTIONS.map(o => [o.value, o.label]));
const AVAILABLE_ROLES = [
  { slug: "admin", label: "Administrador" },
  { slug: "gerente", label: "Gerente" },
  { slug: "rrhh", label: "RRHH" },
  { slug: "jefe_taller", label: "Jefe de taller" },
  { slug: "jefe_recambios", label: "Jefe de recambios" },
  { slug: "asesor", label: "Asesor" },
  { slug: "recepcion", label: "Recepción" },
];

function ApprovalMatrixTab({ axiosAuth, hrConfig, departments = [], centers = [] }) {
  const [matrices, setMatrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", center: "", department: "", request_type: "", steps: [], active: true, priority: 0 });
  const [approverUsers, setApproverUsers] = useState([]);

  const userById = useMemo(() => {
    const m = {};
    for (const u of approverUsers) m[u.id] = u;
    return m;
  }, [approverUsers]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosAuth().get("/hr/approval-matrices");
      setMatrices(res.data);
    } catch { toast.error("Error al cargar matrices"); }
    finally { setLoading(false); }
  }, [axiosAuth]);

  const loadUsers = useCallback(async () => {
    try {
      const r = await axiosAuth().get("/hr/approver-users");
      setApproverUsers(r.data?.users || []);
    } catch { /* silencioso */ }
  }, [axiosAuth]);

  useEffect(() => { load(); loadUsers(); }, [load, loadUsers]);

  const openNew = () => {
    setEditing("new");
    setForm({ name: "", center: "", department: "", request_type: "", steps: [{ order: 1, approver_type: "jefe_directo", approver_value: "jefe_directo", label: "Jefe directo", scope_center: false }], active: true, priority: 0 });
  };

  const openEdit = (m) => {
    setEditing(m.id);
    setForm({ name: m.name, center: m.center, department: m.department, request_type: m.request_type, steps: m.steps || [], active: m.active, priority: m.priority });
  };

  const save = async () => {
    if (!form.name) { toast.error("El nombre es obligatorio"); return; }
    if (form.steps.length === 0) { toast.error("Debe tener al menos un paso"); return; }
    // Pre-validación cliente: ningún paso "Usuario concreto" sin usuario
    for (const s of form.steps) {
      if (s.approver_type === "user" && !s.approver_value) {
        toast.error(`Paso ${s.order}: selecciona un usuario aprobador.`); return;
      }
      if (s.approver_type === "role" && !s.approver_value) {
        toast.error(`Paso ${s.order}: selecciona un rol aprobador.`); return;
      }
    }
    try {
      if (editing === "new") {
        await axiosAuth().post("/hr/approval-matrices", form);
        toast.success("Matriz creada");
      } else {
        await axiosAuth().put(`/hr/approval-matrices/${editing}`, form);
        toast.success("Matriz actualizada");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const remove = async (id) => {
    try {
      await axiosAuth().delete(`/hr/approval-matrices/${id}`);
      toast.success("Eliminada");
      load();
    } catch { toast.error("Error"); }
  };

  const addStep = () => {
    const nextOrder = form.steps.length > 0 ? Math.max(...form.steps.map(s => s.order)) + 1 : 1;
    setForm({ ...form, steps: [...form.steps, { order: nextOrder, approver_type: "jefe_directo", approver_value: "jefe_directo", label: "", scope_center: false }] });
  };

  const updateStep = (idx, field, value) => {
    const steps = [...form.steps];
    steps[idx] = { ...steps[idx], [field]: value };
    setForm({ ...form, steps });
  };

  const changeStepType = (idx, newType) => {
    const steps = [...form.steps];
    const opt = APPROVER_TYPE_OPTIONS.find(o => o.value === newType);
    const defValue = opt?.needs_value ? "" : newType;
    steps[idx] = { ...steps[idx], approver_type: newType, approver_value: defValue };
    setForm({ ...form, steps });
  };

  const removeStep = (idx) => {
    setForm({ ...form, steps: form.steps.filter((_, i) => i !== idx) });
  };

  // Texto legible para mostrar en el badge de la card (fallback al display del backend)
  const humanStepLabel = (s) => {
    if (s.approver_display?.label_human) return s.approver_display.label_human;
    const t = s.approver_type;
    if (t === "jefe_directo") return "Jefe directo del empleado";
    if (t === "gerencia_centro") return "Gerencia del centro";
    if (t === "admin_rrhh") return "Admin / RRHH";
    if (t === "role") return `Rol: ${s.approver_value || "—"}`;
    if (t === "user") {
      const u = userById[s.approver_value];
      if (!u) return "Usuario concreto (no encontrado)";
      return `${u.name} · ${u.email}`;
    }
    return s.label || s.approver_value || "—";
  };

  return (
    <div className="space-y-4" data-testid="hr-approval-matrix-tab">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-500">Define quien aprueba cada solicitud segun centro, departamento y tipo. La matriz mas especifica tiene prioridad.</p>
        </div>
        <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white text-xs rounded-sm" onClick={openNew} data-testid="btn-new-matrix">
          <Plus className="w-3 h-3 mr-1" /> Nueva Matriz
        </Button>
      </div>

      <div className="space-y-3">
        {loading ? <p className="text-center py-8 text-slate-400 text-xs">Cargando...</p> : matrices.length === 0 ? <p className="text-center py-8 text-slate-400 text-xs">Sin matrices de aprobacion</p> : matrices.map(m => (
          <Card key={m.id} className={`border rounded-sm ${m.active ? "border-slate-200" : "border-slate-100 opacity-60"}`} data-testid={`matrix-${m.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-sm font-bold">{m.name}</h3>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {m.center && <Badge variant="outline" className="text-[9px] rounded-sm">Centro: {m.center}</Badge>}
                    {m.department && <Badge variant="outline" className="text-[9px] rounded-sm">Dept: {m.department}</Badge>}
                    {m.request_type && <Badge variant="outline" className="text-[9px] rounded-sm">Tipo: {m.request_type}</Badge>}
                    {!m.center && !m.department && !m.request_type && <Badge variant="outline" className="text-[9px] rounded-sm text-amber-600 border-amber-300">General (fallback)</Badge>}
                    <Badge className="text-[9px] bg-slate-100 text-slate-600">Prioridad: {m.priority}</Badge>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => openEdit(m)}>Editar</Button>
                  <Button variant="ghost" size="sm" className="h-7 text-xs text-red-600" onClick={() => remove(m.id)}>Eliminar</Button>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {(m.steps || []).sort((a, b) => a.order - b.order).map((s, i) => {
                  const warning = s.approver_display?.warning || "";
                  return (
                    <div key={i} className="flex items-center gap-1.5" data-testid={`matrix-${m.id}-step-${s.order}`}>
                      <div className="flex flex-col items-start">
                        {s.label && <span className="text-[9px] uppercase tracking-wider text-slate-400">{s.label}</span>}
                        <div className={`px-2.5 py-1.5 ${warning ? "bg-amber-600" : "bg-slate-900"} text-white rounded-sm text-[10px] font-semibold flex items-center gap-1`} title={warning || ""}>
                          {warning && <AlertTriangle className="w-3 h-3" />}
                          {humanStepLabel(s)}
                        </div>
                        {warning && <span className="text-[9px] text-amber-700 mt-0.5">{warning}</span>}
                      </div>
                      {i < m.steps.length - 1 && <ArrowRight className="w-3.5 h-3.5 text-slate-400" />}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="matrix-dialog">
          <DialogHeader><DialogTitle className="text-sm uppercase tracking-wider">{editing === "new" ? "Nueva Matriz" : "Editar Matriz"}</DialogTitle></DialogHeader>
          <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Nombre *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="h-9 text-xs rounded-sm" placeholder="Ej: Taller - Vacaciones" data-testid="matrix-name" autoComplete="off" name="matrix-display-name" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Centro</Label>
                <Select value={form.center || "_all"} onValueChange={v => setForm({ ...form, center: v === "_all" ? "" : v })}>
                  <SelectTrigger className="h-8 text-xs rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">Todos los centros</SelectItem>
                    {centers.filter(c => c.active).map(c => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Departamento</Label>
                <Select value={form.department || "_all"} onValueChange={v => setForm({ ...form, department: v === "_all" ? "" : v })}>
                  <SelectTrigger className="h-8 text-xs rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">Todos los departamentos</SelectItem>
                    {departments.filter(d => d.active).map(d => <SelectItem key={d.slug} value={d.slug}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Tipo solicitud</Label>
                <Select value={form.request_type || "all"} onValueChange={v => setForm({ ...form, request_type: v === "all" ? "" : v })}>
                  <SelectTrigger className="h-8 text-xs rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {(hrConfig?.request_types || []).map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2 block">Pasos de aprobacion</Label>
              <div className="space-y-2">
                {form.steps.map((s, i) => {
                  const selectedUser = s.approver_type === "user" ? userById[s.approver_value] : null;
                  const userInactiveWarn = selectedUser && !selectedUser.active;
                  return (
                  <div key={i} className="p-2.5 border border-slate-200 rounded-sm bg-slate-50 space-y-2" data-testid={`matrix-step-row-${i}`}>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-slate-900 text-white text-[10px] w-6 h-6 flex items-center justify-center p-0">{s.order}</Badge>
                      <Input value={s.label} onChange={e => updateStep(i, "label", e.target.value)} className="h-7 text-xs rounded-sm flex-1" placeholder="Etiqueta (ej: Jefe Taller)" autoComplete="off" data-testid={`step-${i}-label`} />
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => removeStep(i)}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500 w-full">Aprobador</Label>
                      <Select value={s.approver_type} onValueChange={v => changeStepType(i, v)}>
                        <SelectTrigger className="h-7 text-[10px] flex-1 min-w-[180px] rounded-sm" data-testid={`step-${i}-type`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {APPROVER_TYPE_OPTIONS.map(o => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {s.approver_type === "role" && (
                        <Select value={s.approver_value || ""} onValueChange={v => updateStep(i, "approver_value", v)}>
                          <SelectTrigger className="h-7 text-[10px] flex-1 min-w-[160px] rounded-sm" data-testid={`step-${i}-role-select`}><SelectValue placeholder="Selecciona rol…" /></SelectTrigger>
                          <SelectContent>
                            {AVAILABLE_ROLES.map(r => (
                              <SelectItem key={r.slug} value={r.slug}>{r.label} <span className="text-[10px] text-slate-400">({r.slug})</span></SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      {s.approver_type === "user" && (
                        <Select value={s.approver_value || ""} onValueChange={v => updateStep(i, "approver_value", v)}>
                          <SelectTrigger className="h-7 text-[10px] flex-1 min-w-[260px] rounded-sm" data-testid={`step-${i}-user-select`}><SelectValue placeholder="Selecciona usuario aprobador…" /></SelectTrigger>
                          <SelectContent className="max-h-80">
                            {approverUsers.length === 0 && <div className="text-[10px] text-slate-400 px-2 py-1">Cargando usuarios…</div>}
                            {approverUsers.map(u => (
                              <SelectItem key={u.id} value={u.id}>
                                <div className="flex flex-col">
                                  <span className="text-[11px] font-semibold">{u.name} {!u.active && <span className="text-amber-600">· INACTIVO</span>}</span>
                                  <span className="text-[10px] text-slate-500">{u.email} · {u.role}{u.center ? ` · ${u.center}` : ""}{u.department ? `/${u.department}` : ""}</span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      {(s.approver_type === "jefe_directo" || s.approver_type === "gerencia_centro" || s.approver_type === "admin_rrhh") && (
                        <span className="text-[10px] text-slate-500 italic px-2">Resuelve automáticamente al crear cada solicitud.</span>
                      )}
                    </div>
                    {userInactiveWarn && (
                      <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1 flex items-center gap-1" data-testid={`step-${i}-inactive-warning`}>
                        <AlertTriangle className="w-3 h-3" /> El aprobador seleccionado está inactivo. Selecciona otro o reactívalo antes de guardar.
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
              <Button variant="outline" size="sm" className="text-xs rounded-sm mt-2" onClick={addStep} data-testid="btn-add-step"><Plus className="w-3 h-3 mr-1" /> Anadir paso</Button>
            </div>

            <div className="flex items-center gap-3">
              <Switch checked={form.active} onCheckedChange={v => setForm({ ...form, active: v })} />
              <Label className="text-xs">Activa</Label>
            </div>
          </div>
          </form>
          <DialogFooter>
            <Button variant="outline" size="sm" className="text-xs rounded-sm" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white text-xs rounded-sm" onClick={save} data-testid="btn-save-matrix">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═══════════════════════════════════════════
// BUSINESS RULES TAB
// ═══════════════════════════════════════════
function RulesTab({ axiosAuth }) {
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axiosAuth().get("/hr/business-rules");
      setRules(res.data);
    } catch { toast.error("Error al cargar reglas"); }
  }, [axiosAuth]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await axiosAuth().put("/hr/business-rules", rules);
      toast.success("Reglas guardadas");
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  if (!rules) return <div className="text-center py-8 text-slate-400 text-xs">Cargando...</div>;

  return (
    <div className="space-y-5" data-testid="hr-rules-tab">
      {/* Iter 218 — Banner aclaratorio: separar 'cómo se calculan los días' de 'límites operativos' */}
      <div className="rounded-sm border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900 flex gap-2" data-testid="hr-rules-scope-banner">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>Esta pestaña gestiona <strong>límites operativos</strong> (saldos por defecto, máximos concurrentes, bloqueos, tiempos). Para cambiar <strong>cómo se calculan los días descontados</strong> (laborables / naturales / manual), ve a <strong>Configuración RRHH</strong>.</span>
      </div>
      <div className="flex justify-end">
        <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white text-xs rounded-sm" onClick={save} disabled={saving} data-testid="btn-save-rules">{saving ? "Guardando..." : "Guardar reglas"}</Button>
      </div>

      <Card className="border-slate-200 rounded-sm">
        <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider font-semibold flex items-center gap-2"><Shield className="w-4 h-4 text-red-600" /> Limites de ausencia simultanea</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500">Max por centro</Label>
              <Input type="number" value={rules.max_concurrent_center} onChange={e => setRules({ ...rules, max_concurrent_center: parseInt(e.target.value) || 0 })} className="h-8 text-xs rounded-sm" data-testid="rule-max-center" />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500">Max por departamento</Label>
              <Input type="number" value={rules.max_concurrent_department} onChange={e => setRules({ ...rules, max_concurrent_department: parseInt(e.target.value) || 0 })} className="h-8 text-xs rounded-sm" data-testid="rule-max-dept" />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500">Max por dia (total)</Label>
              <Input type="number" value={rules.max_concurrent_day} onChange={e => setRules({ ...rules, max_concurrent_day: parseInt(e.target.value) || 0 })} className="h-8 text-xs rounded-sm" data-testid="rule-max-day" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 rounded-sm">
        <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider font-semibold flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-600" /> Politicas de bloqueo</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between p-2 border border-slate-100 rounded-sm">
            <div><p className="text-xs font-medium">Bloquear si saldo insuficiente</p><p className="text-[10px] text-slate-400">Impide crear solicitud si excede dias disponibles</p></div>
            <Switch checked={rules.block_on_exceeded_balance} onCheckedChange={v => setRules({ ...rules, block_on_exceeded_balance: v })} data-testid="rule-block-balance" />
          </div>
          <div className="flex items-center justify-between p-2 border border-slate-100 rounded-sm">
            <div><p className="text-xs font-medium">Bloquear sin aprobador</p><p className="text-[10px] text-slate-400">Impide crear solicitud si no hay matriz de aprobacion</p></div>
            <Switch checked={rules.block_on_no_approver} onCheckedChange={v => setRules({ ...rules, block_on_no_approver: v })} data-testid="rule-block-no-approver" />
          </div>
          <div className="flex items-center justify-between p-2 border border-slate-100 rounded-sm">
            <div><p className="text-xs font-medium">Avisar por solapes</p><p className="text-[10px] text-slate-400">Genera alertas cuando se superan maximos de ausencia</p></div>
            <Switch checked={rules.warn_on_overlap} onCheckedChange={v => setRules({ ...rules, warn_on_overlap: v })} data-testid="rule-warn-overlap" />
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 rounded-sm">
        <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider font-semibold flex items-center gap-2"><Clock className="w-4 h-4 text-red-600" /> Tiempos</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Horas para considerar solicitud atrasada</Label>
            <Input type="number" value={rules.overdue_hours} onChange={e => setRules({ ...rules, overdue_hours: parseInt(e.target.value) || 48 })} className="h-8 text-xs rounded-sm w-24" data-testid="rule-overdue-hours" />
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 rounded-sm">
        <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider font-semibold flex items-center gap-2"><Calendar className="w-4 h-4 text-red-600" /> Saldos por defecto (dias/ano)</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-3">
            {Object.entries(rules.balance_defaults || {}).map(([type, days]) => (
              <div key={type} className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 capitalize">{type.replace("_", " ")}</Label>
                <Input type="number" value={days} onChange={e => setRules({ ...rules, balance_defaults: { ...rules.balance_defaults, [type]: parseInt(e.target.value) || 0 } })} className="h-8 text-xs rounded-sm" data-testid={`rule-balance-${type}`} />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════
// CALENDAR TAB (preserved from original)
// ═══════════════════════════════════════════
function CalendarTab({ axiosAuth, hrConfig }) {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [events, setEvents] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [centers, setCenters] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filterCenter, setFilterCenter] = useState("");
  const [filterDept, setFilterDept] = useState("");
  const [filterEmp, setFilterEmp] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const loadCatalogs = useCallback(async () => {
    try {
      const [d, c, e] = await Promise.all([
        axiosAuth().get("/departments"), axiosAuth().get("/centers"), axiosAuth().get("/employees"),
      ]);
      setDepartments(d.data); setCenters(c.data); setEmployees(e.data);
    } catch {
      // ignore catalog fetch errors
    }
  }, [axiosAuth]);
  useEffect(() => { loadCatalogs(); }, [loadCatalogs]);

  const load = useCallback(async () => {
    const params = { month };
    if (filterCenter) params.center_id = filterCenter;
    if (filterDept) params.department_id = filterDept;
    if (filterEmp) params.employee_id = filterEmp;
    if (filterType) params.request_type = filterType;
    if (filterStatus) params.status = filterStatus;
    try {
      const res = await axiosAuth().get("/hr/calendar", { params });
      setEvents(res.data.events || []);
      setHolidays(res.data.holidays || []);
      setConflicts(res.data.conflicts || []);
    } catch {}
  }, [axiosAuth, month, filterCenter, filterDept, filterEmp, filterType, filterStatus]);
  useEffect(() => { load(); }, [load]);

  const year = parseInt(month.split("-")[0]);
  const mon = parseInt(month.split("-")[1]);
  const daysInMonth = new Date(year, mon, 0).getDate();
  const startWeekday = (new Date(year, mon - 1, 1).getDay() + 6) % 7;
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const dayStr = (day) => day ? `${month}-${String(day).padStart(2, "0")}` : "";
  const dayEvents = (day) => { if (!day) return []; const ds = dayStr(day); return events.filter(e => ds >= e.start && ds <= e.end); };
  const dayHolidays = (day) => { if (!day) return []; const ds = dayStr(day); return holidays.filter(h => h.date === ds); };
  const dayConflicts = (day) => { if (!day) return []; const ds = dayStr(day); return conflicts.filter(c => c.date === ds); };

  const prevMonth = () => { const d = new Date(year, mon - 2, 1); setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`); setSelectedDay(null); };
  const nextMonth = () => { const d = new Date(year, mon, 1); setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`); setSelectedDay(null); };
  const monthNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

  const requestTypes = hrConfig?.request_types?.filter(t => t.active) || [];
  const leaveStates = hrConfig?.leave_states || [];
  const calColors = hrConfig?.calendar_colors || {};

  const selEvts = selectedDay ? dayEvents(selectedDay) : [];
  const selHols = selectedDay ? dayHolidays(selectedDay) : [];
  const selConfs = selectedDay ? dayConflicts(selectedDay) : [];
  const selCls = "h-7 text-[10px] rounded-sm";

  const activeFilters = [filterCenter, filterDept, filterEmp, filterType, filterStatus].filter(Boolean).length;

  return (
    <div className="space-y-4" data-testid="hr-calendar-tab">
      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-end" data-testid="cal-filters">
        <Select value={filterCenter || "_none"} onValueChange={v => setFilterCenter(v === "_none" ? "" : v)}>
          <SelectTrigger className={`${selCls} w-32`} data-testid="cal-filter-center"><SelectValue placeholder="Centro" /></SelectTrigger>
          <SelectContent><SelectItem value="_none">Todos centros</SelectItem>{centers.filter(c=>c.active).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filterDept || "_none"} onValueChange={v => setFilterDept(v === "_none" ? "" : v)}>
          <SelectTrigger className={`${selCls} w-36`} data-testid="cal-filter-dept"><SelectValue placeholder="Departamento" /></SelectTrigger>
          <SelectContent><SelectItem value="_none">Todos dept.</SelectItem>{departments.filter(d=>d.active).map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filterEmp || "_none"} onValueChange={v => setFilterEmp(v === "_none" ? "" : v)}>
          <SelectTrigger className={`${selCls} w-40`} data-testid="cal-filter-emp"><SelectValue placeholder="Empleado" /></SelectTrigger>
          <SelectContent className="max-h-60"><SelectItem value="_none">Todos</SelectItem>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filterType || "_none"} onValueChange={v => setFilterType(v === "_none" ? "" : v)}>
          <SelectTrigger className={`${selCls} w-36`} data-testid="cal-filter-type"><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent><SelectItem value="_none">Todos tipos</SelectItem>{requestTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filterStatus || "_none"} onValueChange={v => setFilterStatus(v === "_none" ? "" : v)}>
          <SelectTrigger className={`${selCls} w-32`} data-testid="cal-filter-status"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent><SelectItem value="_none">Todos</SelectItem>{leaveStates.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
        {activeFilters > 0 && (
          <Button variant="ghost" size="sm" className="h-7 text-[10px] text-slate-500" onClick={() => { setFilterCenter(""); setFilterDept(""); setFilterEmp(""); setFilterType(""); setFilterStatus(""); }}>Limpiar ({activeFilters})</Button>
        )}
        <Badge variant="outline" className="text-[9px] h-7 flex items-center">{events.length} ausencias</Badge>
      </div>

      {/* Month nav */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={prevMonth} data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
        <h3 className="text-sm font-bold uppercase tracking-wider">{monthNames[mon - 1]} {year}</h3>
        <Button variant="ghost" size="sm" onClick={nextMonth} data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
      </div>

      <div className="flex gap-4">
        {/* Calendar grid */}
        <div className="flex-1">
          <div className="grid grid-cols-7 gap-px bg-slate-200 border border-slate-200 rounded-sm overflow-hidden" data-testid="hr-calendar">
            {["LU", "MA", "MI", "JU", "VI", "SA", "DO"].map(d => (
              <div key={d} className="bg-slate-100 text-center py-1 text-[10px] font-bold uppercase text-slate-500">{d}</div>
            ))}
            {cells.map((day, i) => {
              const evts = dayEvents(day);
              const hols = dayHolidays(day);
              const confs = dayConflicts(day);
              const isWeekend = day && new Date(year, mon - 1, day).getDay() % 6 === 0;
              const isHoliday = hols.length > 0;
              const hasConflict = confs.length > 0;
              const isSelected = day === selectedDay;
              const today = new Date();
              const isToday = day && today.getFullYear() === year && today.getMonth() + 1 === mon && today.getDate() === day;
              return (
                <div key={i}
                  className={`min-h-[72px] p-1 cursor-pointer transition-colors
                    ${!day ? 'bg-slate-50' : 'bg-white hover:bg-blue-50/50'}
                    ${isWeekend && !isHoliday ? 'bg-slate-50/80' : ''}
                    ${isHoliday ? 'bg-amber-50' : ''}
                    ${isSelected ? 'ring-2 ring-inset ring-blue-500' : ''}
                    ${hasConflict ? 'border-l-2 border-l-red-500' : ''}
                  `}
                  onClick={() => day && setSelectedDay(day === selectedDay ? null : day)}
                  data-testid={day ? `cal-day-${day}` : undefined}
                >
                  {day && (
                    <>
                      <div className="flex items-center justify-between">
                        <span className={`text-[10px] font-mono ${isToday ? 'bg-red-600 text-white w-5 h-5 flex items-center justify-center rounded-full' : 'text-slate-400'}`}>{day}</span>
                        {evts.length > 0 && <span className="text-[9px] font-bold text-slate-600 bg-slate-100 rounded-full w-4 h-4 flex items-center justify-center">{evts.length}</span>}
                      </div>
                      {isHoliday && <div className="text-[7px] text-amber-700 font-semibold truncate mt-0.5">{hols[0].name}</div>}
                      <div className="space-y-px mt-0.5">
                        {evts.slice(0, 3).map(e => (
                          <div key={e.id} className="text-[7px] leading-tight px-1 py-px rounded-sm truncate text-white" style={{ backgroundColor: e.color }}>{e.employee_name.split(" ")[0]}</div>
                        ))}
                        {evts.length > 3 && <span className="text-[7px] text-slate-400 font-medium">+{evts.length - 3} mas</span>}
                      </div>
                      {hasConflict && <AlertTriangle className="w-2.5 h-2.5 text-red-500 mt-0.5" />}
                    </>
                  )}
                </div>
              );
            })}
          </div>
          {/* Legend */}
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 items-center">
            {Object.entries(calColors).map(([k, c]) => (
              <div key={k} className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: c }} /><span className="text-[9px] text-slate-500 capitalize">{k.replace("_", " ")}</span></div>
            ))}
            <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 bg-amber-100 border border-amber-300 rounded-sm" /><span className="text-[9px] text-slate-500">Festivo</span></div>
            <div className="flex items-center gap-1"><div className="w-0.5 h-2.5 bg-red-500 rounded" /><span className="text-[9px] text-slate-500">Conflicto</span></div>
            <div className="flex items-center gap-1"><div className="w-4 h-4 rounded-full bg-red-600 flex items-center justify-center text-white text-[7px]">H</div><span className="text-[9px] text-slate-500">Hoy</span></div>
          </div>
        </div>

        {/* Day detail panel */}
        <div className="w-72 flex-shrink-0">
          <Card className="border-slate-200 rounded-sm sticky top-4">
            <CardContent className="p-3">
              {selectedDay ? (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs font-bold uppercase">{selectedDay} {monthNames[mon - 1]}</h4>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setSelectedDay(null)}><X className="w-3 h-3" /></Button>
                  </div>
                  {/* Holidays */}
                  {selHols.length > 0 && (
                    <div className="mb-3 p-2 bg-amber-50 rounded-sm border border-amber-200">
                      {selHols.map((h, i) => <p key={i} className="text-[10px] font-semibold text-amber-800">{h.name} ({h.type})</p>)}
                    </div>
                  )}
                  {/* Conflicts */}
                  {selConfs.length > 0 && (
                    <div className="mb-3 space-y-1">
                      {selConfs.map((c, i) => (
                        <div key={i} className="p-1.5 bg-red-50 border border-red-200 rounded-sm flex items-start gap-1.5">
                          <AlertTriangle className="w-3 h-3 text-red-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-[10px] font-semibold text-red-700">Conflicto {c.scope}</p>
                            <p className="text-[9px] text-red-600">{c.count} ausencias en {c.scope_name}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Events list */}
                  {selEvts.length === 0 ? (
                    <p className="text-[10px] text-slate-400 text-center py-4">Sin ausencias</p>
                  ) : (
                    <div className="space-y-2" data-testid="day-events-list">
                      {selEvts.map(e => (
                        <div key={e.id} className="p-2 border border-slate-100 rounded-sm" data-testid={`day-event-${e.id}`}>
                          <div className="flex items-center gap-1.5 mb-1">
                            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: e.color }} />
                            <span className="text-[10px] font-bold truncate">{e.employee_name}</span>
                          </div>
                          <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-[9px] text-slate-500">
                            <span>Tipo</span><span className="text-slate-700 font-medium">{e.type_label}</span>
                            <span>Estado</span><span className="text-slate-700">{e.status_label}</span>
                            <span>Centro</span><span className="text-slate-700">{e.center || "-"}</span>
                            <span>Depto</span><span className="text-slate-700">{e.department || "-"}</span>
                            <span>Periodo</span><span className="text-slate-700 font-mono">{e.start} / {e.end}</span>
                          </div>
                          <div className="flex gap-1 mt-1.5">
                            <a href={`/admin/rrhh`} className="text-[8px] text-blue-600 hover:underline">Ver solicitud</a>
                            <span className="text-[8px] text-slate-300">|</span>
                            <a href={`/admin/empleados`} className="text-[8px] text-blue-600 hover:underline">Ver ficha</a>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-[9px] text-slate-400 text-center mt-2">{selEvts.length} ausencia{selEvts.length !== 1 ? "s" : ""}</p>
                </>
              ) : (
                <div className="text-center py-8">
                  <Calendar className="w-6 h-6 text-slate-300 mx-auto mb-2" />
                  <p className="text-[10px] text-slate-400">Selecciona un dia para ver detalle</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// HOLIDAYS TAB (preserved from original)
// ═══════════════════════════════════════════
function HolidaysTab({ axiosAuth }) {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [typeFilter, setTypeFilter] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ date: "", name: "", holiday_type: "nacional", center: "", active: true });
  const { years: availableYears } = useYearRange();
  const load = useCallback(async () => {
    setLoading(true);
    try { const params = {}; if (year) params.year = year; if (typeFilter !== "all") params.holiday_type = typeFilter; const res = await axiosAuth().get("/hr/holidays", { params }); setHolidays(res.data); } catch { toast.error("Error al cargar festivos"); }
    finally { setLoading(false); }
  }, [axiosAuth, year, typeFilter]);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    if (!form.date || !form.name) { toast.error("Fecha y nombre obligatorios"); return; }
    try { if (editing) { await axiosAuth().put(`/hr/holidays/${editing}`, form); toast.success("Actualizado"); } else { await axiosAuth().post("/hr/holidays", form); toast.success("Creado"); } setShowAdd(false); setEditing(null); setForm({ date: "", name: "", holiday_type: "nacional", center: "", active: true }); load(); } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const remove = async (id) => { try { await axiosAuth().delete(`/hr/holidays/${id}`); toast.success("Eliminado"); load(); } catch { toast.error("Error"); } };
  const TYPE_LABELS = { nacional: "Nacional", autonomico: "Autonomico", centro: "Centro" };
  const TYPE_COLORS = { nacional: "#ef4444", autonomico: "#8b5cf6", centro: "#3b82f6" };
  return (
    <div className="space-y-4" data-testid="hr-holidays-tab">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          <Select value={year} onValueChange={setYear}><SelectTrigger className="h-8 w-24 text-xs rounded-sm" data-testid="holiday-year"><SelectValue /></SelectTrigger><SelectContent>{availableYears.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent></Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}><SelectTrigger className="h-8 w-36 text-xs rounded-sm"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Todos</SelectItem><SelectItem value="nacional">Nacional</SelectItem><SelectItem value="autonomico">Autonomico</SelectItem><SelectItem value="centro">Centro</SelectItem></SelectContent></Select>
          <Badge variant="outline" className="text-[10px] rounded-sm">{holidays.length} festivos</Badge>
        </div>
        <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white text-xs rounded-sm" onClick={() => { setEditing(null); setForm({ date: "", name: "", holiday_type: "nacional", center: "", active: true }); setShowAdd(true); }} data-testid="btn-add-holiday"><Plus className="w-3 h-3 mr-1" /> Nuevo</Button>
      </div>
      <Card className="border-slate-200 rounded-sm"><CardContent className="p-0">
        <Table><TableHeader><TableRow className="bg-slate-50"><TableHead className="text-xs">Fecha</TableHead><TableHead className="text-xs">Nombre</TableHead><TableHead className="text-xs">Tipo</TableHead><TableHead className="text-xs">Centro</TableHead><TableHead className="text-xs text-right">Acciones</TableHead></TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400 text-sm">Cargando...</TableCell></TableRow> : holidays.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400 text-sm">Sin festivos</TableCell></TableRow> : holidays.map(h => (
              <TableRow key={h.id} className={`hover:bg-slate-50 ${!h.active ? 'opacity-50' : ''}`}>
                <TableCell className="text-sm font-mono">{h.date}</TableCell>
                <TableCell className="text-sm font-medium">{h.name}</TableCell>
                <TableCell><Badge variant="outline" className="text-[10px]" style={{ color: TYPE_COLORS[h.holiday_type], borderColor: TYPE_COLORS[h.holiday_type] }}>{TYPE_LABELS[h.holiday_type] || h.holiday_type}</Badge></TableCell>
                <TableCell className="text-xs text-slate-500">{h.center || "-"}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setForm({ date: h.date, name: h.name, holiday_type: h.holiday_type, center: h.center || "", active: h.active }); setEditing(h.id); setShowAdd(true); }}>Editar</Button>
                  <Button variant="ghost" size="sm" className="h-7 text-xs text-red-600" onClick={() => remove(h.id)}>Eliminar</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
      <Dialog open={showAdd} onOpenChange={v => { setShowAdd(v); if (!v) setEditing(null); }}>
        <DialogContent className="max-w-md rounded-sm" data-testid="holiday-dialog">
          <DialogHeader><DialogTitle className="text-sm uppercase">{editing ? "Editar Festivo" : "Nuevo Festivo"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-[10px] uppercase text-slate-500">Fecha *</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="h-9 text-xs rounded-sm" data-testid="holiday-date-input" /></div>
              <div className="space-y-1"><Label className="text-[10px] uppercase text-slate-500">Tipo</Label><Select value={form.holiday_type} onValueChange={v => setForm({ ...form, holiday_type: v })}><SelectTrigger className="h-9 text-xs rounded-sm"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="nacional">Nacional</SelectItem><SelectItem value="autonomico">Autonomico</SelectItem><SelectItem value="centro">Centro</SelectItem></SelectContent></Select></div>
            </div>
            <div className="space-y-1"><Label className="text-[10px] uppercase text-slate-500">Nombre *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="h-9 text-xs rounded-sm" placeholder="Ej: Dia de la Constitucion" data-testid="holiday-name-input" /></div>
            {form.holiday_type === "centro" && <div className="space-y-1"><Label className="text-[10px] uppercase text-slate-500">Centro</Label><Input value={form.center} onChange={e => setForm({ ...form, center: e.target.value })} className="h-9 text-xs rounded-sm" /></div>}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" className="text-xs rounded-sm" onClick={() => { setShowAdd(false); setEditing(null); }}>Cancelar</Button>
            <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white text-xs rounded-sm" onClick={save} data-testid="holiday-save-btn">{editing ? "Actualizar" : "Crear"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═══════════════════════════════════════════
// CONFIG TAB (preserved with updates)
// ═══════════════════════════════════════════
function ConfigTab({ axiosAuth, hrConfig, onReload }) {
  const [cfg, setCfg] = useState(hrConfig || {});
  const [saving, setSaving] = useState(false);
  useEffect(() => { setCfg(hrConfig || {}); }, [hrConfig]);
  const save = async () => {
    setSaving(true);
    try { await axiosAuth().put("/hr/config", cfg); toast.success("Configuracion RRHH guardada. Los cambios afectan a nuevas solicitudes."); onReload(); } catch { toast.error("Error"); }
    finally { setSaving(false); }
  };
  const updateType = (i, field, value) => {
    const arr = [...cfg.request_types];
    arr[i] = { ...arr[i], [field]: value };
    setCfg({ ...cfg, request_types: arr });
  };
  return (
    <div className="space-y-5" data-testid="hr-config-tab">
      <div className="flex justify-end"><Button size="sm" className="bg-red-600 hover:bg-red-700 text-white text-xs rounded-sm" onClick={save} disabled={saving} data-testid="btn-save-hr-config">{saving ? "Guardando..." : "Guardar"}</Button></div>
      <Card className="border-slate-200 rounded-sm">
        <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider font-semibold flex items-center gap-2"><Briefcase className="w-4 h-4 text-red-600" /> Tipos de Solicitud</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <div className="p-2 bg-amber-50 border border-amber-200 rounded-sm mb-2">
            <p className="text-[10px] text-amber-700 font-medium">Los cambios en estos ajustes solo afectan a nuevas solicitudes. Las solicitudes existentes mantienen su configuracion original.</p>
          </div>
          {(cfg.request_types || []).map((t, i) => (
            <div key={i} className="p-3 border border-slate-100 rounded-sm space-y-2" data-testid={`config-type-${t.value}`}>
              <div className="flex items-center gap-3">
                <Input value={t.label} onChange={e => updateType(i, "label", e.target.value)} className="h-7 text-xs rounded-sm flex-1" data-testid={`type-label-${t.value}`} />
                <Badge className="text-[9px] bg-slate-100 text-slate-500 rounded-sm">{t.value}</Badge>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-1.5">
                  <Switch checked={t.active} onCheckedChange={v => updateType(i, "active", v)} data-testid={`type-active-${t.value}`} />
                  <span className="text-[10px] text-slate-500 font-medium">Activo</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Switch checked={t.requires_approval ?? false} onCheckedChange={v => updateType(i, "requires_approval", v)} data-testid={`type-approval-${t.value}`} />
                  <span className={`text-[10px] font-medium ${t.requires_approval ? "text-blue-600" : "text-slate-400"}`}>Requiere aprobacion</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Switch checked={t.requires_docs ?? false} onCheckedChange={v => updateType(i, "requires_docs", v)} data-testid={`type-docs-${t.value}`} />
                  <span className={`text-[10px] font-medium ${t.requires_docs ? "text-amber-600" : "text-slate-400"}`}>Requiere documentacion</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Switch checked={t.doc_required_to_close ?? false} onCheckedChange={v => updateType(i, "doc_required_to_close", v)} data-testid={`type-doc-close-${t.value}`} />
                  <span className={`text-[10px] font-medium ${t.doc_required_to_close ? "text-red-600" : "text-slate-400"}`}>Doc. obligatoria para cerrar</span>
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
      <Card className="border-slate-200 rounded-sm">
        <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider font-semibold flex items-center gap-2"><Calendar className="w-4 h-4 text-red-600" /> Colores del Calendario</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {Object.entries(cfg.calendar_colors || {}).map(([k, c]) => (
              <div key={k} className="flex items-center gap-2"><input type="color" value={c} onChange={e => setCfg({ ...cfg, calendar_colors: { ...cfg.calendar_colors, [k]: e.target.value } })} className="w-8 h-8 rounded-sm cursor-pointer border-0" /><span className="text-xs text-slate-600 capitalize">{k.replace("_", " ")}</span></div>
            ))}
          </div>
        </CardContent>
      </Card>
      <Card className="border-slate-200 rounded-sm">
        <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider font-semibold flex items-center gap-2"><FileText className="w-4 h-4 text-red-600" /> Instrucciones para Empleados</CardTitle></CardHeader>
        <CardContent><Textarea value={cfg.employee_instructions || ""} onChange={e => setCfg({ ...cfg, employee_instructions: e.target.value })} className="text-xs rounded-sm h-20" /></CardContent>
      </Card>
      <Card className="border-slate-200 rounded-sm">
        <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider font-semibold flex items-center gap-2"><Mail className="w-4 h-4 text-red-600" /> Emails RRHH</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 mb-3"><Switch checked={cfg.hr_email_enabled ?? true} onCheckedChange={v => setCfg({ ...cfg, hr_email_enabled: v })} /><Label className="text-xs font-semibold">Emails activos</Label></div>
          <div className="space-y-2">
            {Object.entries(cfg.hr_email_events || {}).map(([key, evt]) => (
              <div key={key} className="flex items-center gap-2 p-2 border border-slate-100 rounded-sm">
                <Switch checked={evt.enabled !== false} onCheckedChange={v => setCfg({ ...cfg, hr_email_events: { ...cfg.hr_email_events, [key]: { ...evt, enabled: v } } })} />
                <span className="text-xs text-slate-700 w-32 flex-shrink-0">{key}</span>
                <Input value={evt.subject || ""} onChange={e => setCfg({ ...cfg, hr_email_events: { ...cfg.hr_email_events, [key]: { ...evt, subject: e.target.value } } })} className="h-7 text-[11px] rounded-sm flex-1" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════
// SIMPLE CATALOG SUB-TAB (reusable for Prof Categories, Contrib Groups, Agreements, Cost Centers)
// ═══════════════════════════════════════════
function SimpleCatalogList({ axiosAuth, endpoint, entityLabel, items, onReload }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", active: true, sort_order: 0 });

  const save = async () => {
    if (!form.name) { toast.error("El nombre es obligatorio"); return; }
    try {
      if (editing === "new") await axiosAuth().post(`/${endpoint}`, form);
      else await axiosAuth().put(`/${endpoint}/${editing}`, form);
      toast.success("Guardado"); setEditing(null); onReload();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const remove = async (id) => {
    try { await axiosAuth().delete(`/${endpoint}/${id}`); toast.success("Eliminado"); onReload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white text-xs rounded-sm"
          onClick={() => { setEditing("new"); setForm({ name: "", description: "", active: true, sort_order: 0 }); }}
          data-testid={`btn-new-${endpoint}`}><Plus className="w-3 h-3 mr-1" /> Nuevo {entityLabel}</Button>
      </div>
      <div className="space-y-2">
        {items.map(item => (
          <Card key={item.id} className={`border rounded-sm ${item.active ? "border-slate-200" : "border-slate-100 opacity-60"}`} data-testid={`catalog-${item.slug}`}>
            <CardContent className="p-3 flex items-center gap-3">
              <div className="w-7 h-7 bg-slate-800 text-white rounded-sm flex items-center justify-center text-[10px] font-bold flex-shrink-0">{item.name.charAt(0)}</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold">{item.name}</p>
                {item.description && <p className="text-[10px] text-slate-400">{item.description}</p>}
                {!item.active && <Badge variant="outline" className="text-[8px] text-orange-500 border-orange-300">Inactivo</Badge>}
              </div>
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => {
                setEditing(item.id);
                setForm({ name: item.name, description: item.description || "", active: item.active, sort_order: item.sort_order || 0 });
              }}>Editar</Button>
              <Button variant="ghost" size="sm" className="h-7 text-xs text-red-600" onClick={() => remove(item.id)}>Eliminar</Button>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <p className="text-center text-xs text-slate-400 py-8">Sin {entityLabel.toLowerCase()}s configurados</p>}
      </div>
      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="max-w-md" data-testid={`${endpoint}-dialog`}>
          <DialogHeader><DialogTitle className="text-sm uppercase">{editing === "new" ? `Nuevo ${entityLabel}` : `Editar ${entityLabel}`}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label className="text-[10px] uppercase text-slate-500">Nombre *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="h-8 text-xs rounded-sm" data-testid={`${endpoint}-name`} /></div>
            <div className="space-y-1"><Label className="text-[10px] uppercase text-slate-500">Descripcion</Label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="h-8 text-xs rounded-sm" /></div>
            <div className="flex items-center gap-3"><Switch checked={form.active ?? true} onCheckedChange={v => setForm({ ...form, active: v })} /><Label className="text-xs">Activo</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" className="text-xs rounded-sm" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white text-xs rounded-sm" onClick={save} data-testid={`${endpoint}-save`}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═══════════════════════════════════════════
// CATALOGOS TAB (Departments, Centers, Positions + new catalogs)
// ═══════════════════════════════════════════
function CatalogosTab({ axiosAuth, departments, centers, onReload }) {
  const [positions, setPositions] = useState([]);
  const [profCategories, setProfCategories] = useState([]);
  const [contribGroups, setContribGroups] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [catTab, setCatTab] = useState("departments");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [employees, setEmployees] = useState([]);

  const loadPositions = useCallback(async () => {
    try { const res = await axiosAuth().get("/positions"); setPositions(res.data); } catch {}
  }, [axiosAuth]);
  const loadEmployees = useCallback(async () => {
    try { const res = await axiosAuth().get("/employees"); setEmployees(res.data); } catch {}
  }, [axiosAuth]);
  const loadNewCatalogs = useCallback(async () => {
    try {
      const [pcRes, cgRes, agrRes, ccRes] = await Promise.all([
        axiosAuth().get("/professional-categories"),
        axiosAuth().get("/contribution-groups"),
        axiosAuth().get("/agreements"),
        axiosAuth().get("/cost-centers"),
      ]);
      setProfCategories(pcRes.data);
      setContribGroups(cgRes.data);
      setAgreements(agrRes.data);
      setCostCenters(ccRes.data);
    } catch {}
  }, [axiosAuth]);
  useEffect(() => { loadPositions(); loadEmployees(); loadNewCatalogs(); }, [loadPositions, loadEmployees, loadNewCatalogs]);

  const saveDept = async () => {
    try {
      if (editing === "new") await axiosAuth().post("/departments", form);
      else await axiosAuth().put(`/departments/${editing}`, form);
      toast.success("Guardado"); setEditing(null); onReload();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const saveCenter = async () => {
    try {
      if (editing === "new") await axiosAuth().post("/centers", form);
      else await axiosAuth().put(`/centers/${editing}`, form);
      toast.success("Guardado"); setEditing(null); onReload();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const savePosition = async () => {
    try {
      if (editing === "new") await axiosAuth().post("/positions", form);
      else await axiosAuth().put(`/positions/${editing}`, form);
      toast.success("Guardado"); setEditing(null); loadPositions();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const deleteDept = async (id) => { try { await axiosAuth().delete(`/departments/${id}`); toast.success("Eliminado"); onReload(); } catch (e) { toast.error(e.response?.data?.detail || "Error"); } };
  const deleteCenter = async (id) => { try { await axiosAuth().delete(`/centers/${id}`); toast.success("Eliminado"); onReload(); } catch (e) { toast.error(e.response?.data?.detail || "Error"); } };
  const deletePos = async (id) => { try { await axiosAuth().delete(`/positions/${id}`); toast.success("Eliminado"); loadPositions(); } catch (e) { toast.error(e.response?.data?.detail || "Error"); } };

  const deptMap = {}; departments.forEach(d => { deptMap[d.id] = d; });
  const empMap = {}; employees.forEach(e => { empMap[e.id] = e; });

  return (
    <div className="space-y-4" data-testid="hr-catalogos-tab">
      <Tabs value={catTab} onValueChange={setCatTab}>
        <TabsList className="bg-slate-100 rounded-sm h-8 flex-wrap">
          <TabsTrigger value="departments" className="text-[10px] uppercase tracking-wider rounded-sm data-[state=active]:bg-white">Departamentos ({departments.length})</TabsTrigger>
          <TabsTrigger value="centers" className="text-[10px] uppercase tracking-wider rounded-sm data-[state=active]:bg-white">Centros ({centers.length})</TabsTrigger>
          <TabsTrigger value="positions" className="text-[10px] uppercase tracking-wider rounded-sm data-[state=active]:bg-white">Puestos ({positions.length})</TabsTrigger>
          <TabsTrigger value="prof_categories" className="text-[10px] uppercase tracking-wider rounded-sm data-[state=active]:bg-white">Categorias ({profCategories.length})</TabsTrigger>
          <TabsTrigger value="contrib_groups" className="text-[10px] uppercase tracking-wider rounded-sm data-[state=active]:bg-white">Cotizacion ({contribGroups.length})</TabsTrigger>
          <TabsTrigger value="agreements" className="text-[10px] uppercase tracking-wider rounded-sm data-[state=active]:bg-white">Convenios ({agreements.length})</TabsTrigger>
          <TabsTrigger value="cost_centers" className="text-[10px] uppercase tracking-wider rounded-sm data-[state=active]:bg-white">Centros Coste ({costCenters.length})</TabsTrigger>
        </TabsList>

        {/* Departments */}
        <TabsContent value="departments" className="mt-3">
          <div className="flex justify-end mb-3">
            <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white text-xs rounded-sm" onClick={() => { setEditing("new"); setForm({ name: "", description: "", manager_id: "", center_id: "", active: true, sort_order: 0 }); }} data-testid="btn-new-dept"><Plus className="w-3 h-3 mr-1" /> Nuevo Departamento</Button>
          </div>
          <div className="space-y-2">
            {departments.map(d => (
              <Card key={d.id} className={`border rounded-sm ${d.active ? "border-slate-200" : "border-slate-100 opacity-60"}`} data-testid={`dept-${d.slug}`}>
                <CardContent className="p-3 flex items-center gap-3">
                  <div className="w-8 h-8 bg-slate-900 text-white rounded-sm flex items-center justify-center text-xs font-bold flex-shrink-0">{d.name.charAt(0)}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold">{d.name}</p>
                    <div className="flex gap-2 text-[10px] text-slate-400 mt-0.5">
                      <span>slug: {d.slug}</span>
                      {d.manager_id && empMap[d.manager_id] && <span>Jefe: {empMap[d.manager_id].name}</span>}
                      {!d.active && <Badge variant="outline" className="text-[8px] text-orange-500 border-orange-300">Inactivo</Badge>}
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setEditing(d.id); setForm({ name: d.name, description: d.description || "", manager_id: d.manager_id || "", center_id: d.center_id || "", active: d.active, sort_order: d.sort_order || 0 }); setCatTab("departments"); }}>Editar</Button>
                  <Button variant="ghost" size="sm" className="h-7 text-xs text-red-600" onClick={() => deleteDept(d.id)}>Eliminar</Button>
                </CardContent>
              </Card>
            ))}
          </div>
          <Dialog open={!!editing && catTab === "departments"} onOpenChange={() => setEditing(null)}>
            <DialogContent className="max-w-md" data-testid="dept-dialog">
              <DialogHeader><DialogTitle className="text-sm uppercase">{editing === "new" ? "Nuevo Departamento" : "Editar Departamento"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1"><Label className="text-[10px] uppercase text-slate-500">Nombre *</Label><Input value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} className="h-8 text-xs rounded-sm" data-testid="dept-name" /></div>
                <div className="space-y-1"><Label className="text-[10px] uppercase text-slate-500">Descripcion</Label><Input value={form.description || ""} onChange={e => setForm({ ...form, description: e.target.value })} className="h-8 text-xs rounded-sm" /></div>
                <div className="space-y-1"><Label className="text-[10px] uppercase text-slate-500">Centro</Label>
                  <Select value={form.center_id || "_none"} onValueChange={v => setForm({ ...form, center_id: v === "_none" ? "" : v })}>
                    <SelectTrigger className="h-8 text-xs rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="_none">- Sin centro -</SelectItem>{centers.filter(c=>c.active).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-1"><Label className="text-[10px] uppercase text-slate-500">Responsable</Label>
                  <Select value={form.manager_id || "_none"} onValueChange={v => setForm({ ...form, manager_id: v === "_none" ? "" : v })}>
                    <SelectTrigger className="h-8 text-xs rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="_none">- Sin responsable -</SelectItem>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="flex items-center gap-3"><Switch checked={form.active ?? true} onCheckedChange={v => setForm({ ...form, active: v })} /><Label className="text-xs">Activo</Label></div>
              </div>
              <DialogFooter><Button variant="outline" size="sm" className="text-xs rounded-sm" onClick={() => setEditing(null)}>Cancelar</Button><Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white text-xs rounded-sm" onClick={saveDept} data-testid="dept-save">Guardar</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Centers */}
        <TabsContent value="centers" className="mt-3">
          <div className="flex justify-end mb-3">
            <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white text-xs rounded-sm" onClick={() => { setEditing("new"); setForm({ name: "", address: "", city: "", phone: "", active: true, sort_order: 0 }); setCatTab("centers"); }} data-testid="btn-new-center"><Plus className="w-3 h-3 mr-1" /> Nuevo Centro</Button>
          </div>
          <div className="space-y-2">
            {centers.map(c => (
              <Card key={c.id} className={`border rounded-sm ${c.active ? "border-slate-200" : "border-slate-100 opacity-60"}`} data-testid={`center-${c.slug}`}>
                <CardContent className="p-3 flex items-center gap-3">
                  <Building2 className="w-5 h-5 text-slate-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold">{c.name}</p>
                    <p className="text-[10px] text-slate-400">{c.address || c.city || c.slug}</p>
                  </div>
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setEditing(c.id); setForm({ name: c.name, address: c.address || "", city: c.city || "", phone: c.phone || "", active: c.active, sort_order: c.sort_order || 0 }); setCatTab("centers"); }}>Editar</Button>
                  <Button variant="ghost" size="sm" className="h-7 text-xs text-red-600" onClick={() => deleteCenter(c.id)}>Eliminar</Button>
                </CardContent>
              </Card>
            ))}
          </div>
          <Dialog open={!!editing && catTab === "centers"} onOpenChange={() => setEditing(null)}>
            <DialogContent className="max-w-md" data-testid="center-dialog">
              <DialogHeader><DialogTitle className="text-sm uppercase">{editing === "new" ? "Nuevo Centro" : "Editar Centro"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1"><Label className="text-[10px] uppercase text-slate-500">Nombre *</Label><Input value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} className="h-8 text-xs rounded-sm" data-testid="center-name" /></div>
                <div className="space-y-1"><Label className="text-[10px] uppercase text-slate-500">Direccion</Label><Input value={form.address || ""} onChange={e => setForm({ ...form, address: e.target.value })} className="h-8 text-xs rounded-sm" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label className="text-[10px] uppercase text-slate-500">Ciudad</Label><Input value={form.city || ""} onChange={e => setForm({ ...form, city: e.target.value })} className="h-8 text-xs rounded-sm" /></div>
                  <div className="space-y-1"><Label className="text-[10px] uppercase text-slate-500">Telefono</Label><Input value={form.phone || ""} onChange={e => setForm({ ...form, phone: e.target.value })} className="h-8 text-xs rounded-sm" /></div>
                </div>
                <div className="flex items-center gap-3"><Switch checked={form.active ?? true} onCheckedChange={v => setForm({ ...form, active: v })} /><Label className="text-xs">Activo</Label></div>
              </div>
              <DialogFooter><Button variant="outline" size="sm" className="text-xs rounded-sm" onClick={() => setEditing(null)}>Cancelar</Button><Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white text-xs rounded-sm" onClick={saveCenter} data-testid="center-save">Guardar</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Positions */}
        <TabsContent value="positions" className="mt-3">
          <div className="flex justify-end mb-3">
            <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white text-xs rounded-sm" onClick={() => { setEditing("new"); setForm({ name: "", department_id: "", is_manager: false, description: "", active: true, sort_order: 0 }); setCatTab("positions"); }} data-testid="btn-new-position"><Plus className="w-3 h-3 mr-1" /> Nuevo Puesto</Button>
          </div>
          <div className="space-y-2">
            {positions.map(p => (
              <Card key={p.id} className={`border rounded-sm ${p.active ? "border-slate-200" : "border-slate-100 opacity-60"}`} data-testid={`pos-${p.slug}`}>
                <CardContent className="p-3 flex items-center gap-3">
                  <Briefcase className="w-4 h-4 text-slate-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold">{p.name} {p.is_manager && <Badge className="text-[8px] bg-blue-100 text-blue-700 ml-1">Mando</Badge>}</p>
                    <p className="text-[10px] text-slate-400">{deptMap[p.department_id]?.name || "Sin departamento"}</p>
                  </div>
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setEditing(p.id); setForm({ name: p.name, department_id: p.department_id || "", is_manager: p.is_manager || false, description: p.description || "", active: p.active, sort_order: p.sort_order || 0 }); setCatTab("positions"); }}>Editar</Button>
                  <Button variant="ghost" size="sm" className="h-7 text-xs text-red-600" onClick={() => deletePos(p.id)}>Eliminar</Button>
                </CardContent>
              </Card>
            ))}
            {positions.length === 0 && <p className="text-center text-xs text-slate-400 py-8">Sin puestos configurados</p>}
          </div>
          <Dialog open={!!editing && catTab === "positions"} onOpenChange={() => setEditing(null)}>
            <DialogContent className="max-w-md" data-testid="position-dialog">
              <DialogHeader><DialogTitle className="text-sm uppercase">{editing === "new" ? "Nuevo Puesto" : "Editar Puesto"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1"><Label className="text-[10px] uppercase text-slate-500">Nombre *</Label><Input value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} className="h-8 text-xs rounded-sm" data-testid="position-name" /></div>
                <div className="space-y-1"><Label className="text-[10px] uppercase text-slate-500">Departamento</Label>
                  <Select value={form.department_id || "_none"} onValueChange={v => setForm({ ...form, department_id: v === "_none" ? "" : v })}>
                    <SelectTrigger className="h-8 text-xs rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="_none">- Sin departamento -</SelectItem>{departments.filter(d=>d.active).map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-1"><Label className="text-[10px] uppercase text-slate-500">Descripcion</Label><Input value={form.description || ""} onChange={e => setForm({ ...form, description: e.target.value })} className="h-8 text-xs rounded-sm" /></div>
                <div className="flex items-center gap-3"><Switch checked={form.is_manager ?? false} onCheckedChange={v => setForm({ ...form, is_manager: v })} /><Label className="text-xs">Puesto de mando</Label></div>
                <div className="flex items-center gap-3"><Switch checked={form.active ?? true} onCheckedChange={v => setForm({ ...form, active: v })} /><Label className="text-xs">Activo</Label></div>
              </div>
              <DialogFooter><Button variant="outline" size="sm" className="text-xs rounded-sm" onClick={() => setEditing(null)}>Cancelar</Button><Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white text-xs rounded-sm" onClick={savePosition} data-testid="position-save">Guardar</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* New Catalogs */}
        <TabsContent value="prof_categories" className="mt-3">
          <SimpleCatalogList axiosAuth={axiosAuth} endpoint="professional-categories" entityLabel="Categoria Profesional" items={profCategories} onReload={loadNewCatalogs} />
        </TabsContent>
        <TabsContent value="contrib_groups" className="mt-3">
          <SimpleCatalogList axiosAuth={axiosAuth} endpoint="contribution-groups" entityLabel="Grupo de Cotizacion" items={contribGroups} onReload={loadNewCatalogs} />
        </TabsContent>
        <TabsContent value="agreements" className="mt-3">
          <SimpleCatalogList axiosAuth={axiosAuth} endpoint="agreements" entityLabel="Convenio" items={agreements} onReload={loadNewCatalogs} />
        </TabsContent>
        <TabsContent value="cost_centers" className="mt-3">
          <SimpleCatalogList axiosAuth={axiosAuth} endpoint="cost-centers" entityLabel="Centro de Coste" items={costCenters} onReload={loadNewCatalogs} />
        </TabsContent>
      </Tabs>
    </div>
  );
}


// ═══════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════
export default function HrPage() {
  const { axiosAuth, user } = useAuth();
  // Iter 188 (P1) — soporte ?tab= en la URL para deep-link al sidebar
  const initialTab = (() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const t = sp.get("tab");
      const valid = ["dashboard", "pendientes-mias", "solicitudes", "aprobadores", "reglas",
                     "calendario", "festivos", "documentos", "config", "catalogos"];
      return valid.includes(t) ? t : "dashboard";
    } catch { return "dashboard"; }
  })();
  const [tab, setTab] = useState(initialTab);
  const [hrConfig, setHrConfig] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [centers, setCenters] = useState([]);
  const [activeKpi, setActiveKpi] = useState(null);
  const [kpiFilter, setKpiFilter] = useState(null);

  const loadConfig = useCallback(async () => {
    try {
      const [cfgRes, deptRes, cenRes] = await Promise.all([
        axiosAuth().get("/hr/config"),
        axiosAuth().get("/departments"),
        axiosAuth().get("/centers"),
      ]);
      setHrConfig(cfgRes.data);
      setDepartments(deptRes.data);
      setCenters(cenRes.data);
    } catch {}
  }, [axiosAuth]);
  useEffect(() => { loadConfig(); }, [loadConfig]);

  const handleKpiClick = useCallback((kpiId) => {
    if (activeKpi === kpiId) {
      // Toggle off - clear filter and go back to dashboard
      setActiveKpi(null);
      setKpiFilter(null);
      return;
    }
    setActiveKpi(kpiId);
    const kpiMap = {
      pendientes: { tab: "solicitudes", filter: "pendientes" },
      ausentes: { tab: "calendario", filter: "ausentes" },
      semana: { tab: "calendario", filter: "semana" },
      bajas: { tab: "solicitudes", filter: "bajas_activas" },
      alertas: { tab: "dashboard", filter: "alertas" },
      docs: { tab: "documentos", filter: "docs_pending" },
    };
    const target = kpiMap[kpiId];
    if (target) {
      setKpiFilter(target.filter);
      setTab(target.tab);
    }
  }, [activeKpi]);

  // Clear KPI filter when user manually changes tab
  const handleTabChange = useCallback((newTab) => {
    setTab(newTab);
    if (activeKpi) {
      setActiveKpi(null);
      setKpiFilter(null);
    }
  }, [activeKpi]);

  return (
    <div className="space-y-5" data-testid="hr-page">
      <div>
        <h1 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 uppercase" data-testid="hr-title">RRHH - Gestion Integral</h1>
        <p className="text-sm text-slate-500 mt-0.5">Vacaciones, ausencias, bajas, aprobaciones y alertas</p>
      </div>

      <Tabs value={tab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="bg-slate-100 rounded-sm h-9 flex-wrap" data-testid="hr-tabs">
          <TabsTrigger value="dashboard" className="text-xs uppercase tracking-wider rounded-sm data-[state=active]:bg-white"><LayoutDashboard className="w-3.5 h-3.5 mr-1" /> Dashboard</TabsTrigger>
          <TabsTrigger value="pendientes-mias" className="text-xs uppercase tracking-wider rounded-sm data-[state=active]:bg-white" data-testid="tab-pendientes-mias"><Bell className="w-3.5 h-3.5 mr-1" /> Pendientes mías</TabsTrigger>
          <TabsTrigger value="solicitudes" className="text-xs uppercase tracking-wider rounded-sm data-[state=active]:bg-white"><Briefcase className="w-3.5 h-3.5 mr-1" /> Solicitudes</TabsTrigger>
          <TabsTrigger value="saldos" className="text-xs uppercase tracking-wider rounded-sm data-[state=active]:bg-white" data-testid="tab-saldos"><Palmtree className="w-3.5 h-3.5 mr-1" /> Saldos</TabsTrigger>
          <TabsTrigger value="aprobadores" className="text-xs uppercase tracking-wider rounded-sm data-[state=active]:bg-white"><Shield className="w-3.5 h-3.5 mr-1" /> Aprobadores</TabsTrigger>
          <TabsTrigger value="reglas" className="text-xs uppercase tracking-wider rounded-sm data-[state=active]:bg-white"><AlertTriangle className="w-3.5 h-3.5 mr-1" /> Reglas</TabsTrigger>
          <TabsTrigger value="calendario" className="text-xs uppercase tracking-wider rounded-sm data-[state=active]:bg-white"><Calendar className="w-3.5 h-3.5 mr-1" /> Calendario</TabsTrigger>
          <TabsTrigger value="festivos" className="text-xs uppercase tracking-wider rounded-sm data-[state=active]:bg-white"><CalendarDays className="w-3.5 h-3.5 mr-1" /> Festivos</TabsTrigger>
          <TabsTrigger value="documentos" className="text-xs uppercase tracking-wider rounded-sm data-[state=active]:bg-white"><FolderOpen className="w-3.5 h-3.5 mr-1" /> Documentos</TabsTrigger>
          <TabsTrigger value="config" className="text-xs uppercase tracking-wider rounded-sm data-[state=active]:bg-white"><Settings className="w-3.5 h-3.5 mr-1" /> Config</TabsTrigger>
          <TabsTrigger value="catalogos" className="text-xs uppercase tracking-wider rounded-sm data-[state=active]:bg-white"><Building2 className="w-3.5 h-3.5 mr-1" /> Catalogos</TabsTrigger>
        </TabsList>

        {/* Active KPI filter badge */}
        {activeKpi && (
          <div className="mt-2 flex items-center gap-2" data-testid="kpi-filter-badge">
            <span className="text-[10px] bg-red-50 text-red-700 border border-red-200 px-2.5 py-1 rounded-sm font-semibold uppercase tracking-wider">
              Filtro activo: {activeKpi === "pendientes" ? "Pendientes" : activeKpi === "ausentes" ? "Ausentes hoy" : activeKpi === "semana" ? "Semana" : activeKpi === "bajas" ? "Bajas activas" : activeKpi === "alertas" ? "Alertas" : "Docs por validar"}
            </span>
            <button onClick={() => { setActiveKpi(null); setKpiFilter(null); }} className="text-[10px] text-red-500 hover:text-red-700 font-semibold" data-testid="kpi-clear-filter">
              Quitar filtro
            </button>
          </div>
        )}

        <TabsContent value="dashboard" className="mt-4">{hrConfig && <DashboardTab axiosAuth={axiosAuth} hrConfig={hrConfig} onKpiClick={handleKpiClick} activeKpi={activeKpi} />}</TabsContent>
        <TabsContent value="pendientes-mias" className="mt-4"><PendientesMiasTab axiosAuth={axiosAuth} user={user} onAfterAction={() => {}} /></TabsContent>
        <TabsContent value="solicitudes" className="mt-4">{hrConfig && <RequestsTab axiosAuth={axiosAuth} hrConfig={hrConfig} departments={departments} centers={centers} initialFilter={kpiFilter} />}</TabsContent>
        <TabsContent value="saldos" className="mt-4"><BalancesTab axiosAuth={axiosAuth} departments={departments} centers={centers} /></TabsContent>
        <TabsContent value="aprobadores" className="mt-4">{hrConfig && <ApprovalMatrixTab axiosAuth={axiosAuth} hrConfig={hrConfig} departments={departments} centers={centers} />}</TabsContent>
        <TabsContent value="reglas" className="mt-4"><RulesTab axiosAuth={axiosAuth} /></TabsContent>
        <TabsContent value="calendario" className="mt-4">{hrConfig && <CalendarTab axiosAuth={axiosAuth} hrConfig={hrConfig} />}</TabsContent>
        <TabsContent value="festivos" className="mt-4"><HolidaysTab axiosAuth={axiosAuth} /></TabsContent>
        <TabsContent value="documentos" className="mt-4"><DocumentsTab axiosAuth={axiosAuth} departments={departments} centers={centers} initialFilter={kpiFilter} /></TabsContent>
        <TabsContent value="config" className="mt-4">{hrConfig && <ConfigTab axiosAuth={axiosAuth} hrConfig={hrConfig} onReload={loadConfig} />}</TabsContent>
        <TabsContent value="catalogos" className="mt-4"><CatalogosTab axiosAuth={axiosAuth} departments={departments} centers={centers} onReload={loadConfig} /></TabsContent>
      </Tabs>
    </div>
  );
}


// ═══════════════════════════════════════════
// Iter 197 — Acciones administrativas sobre solicitudes
// ═══════════════════════════════════════════
function AdminVacationActions({ axiosAuth, req, isSuperAdmin, onChanged }) {
  const [mode, setMode] = useState(null); // "edit" | "cancel" | "delete"
  const [reason, setReason] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [edit, setEdit] = useState({
    start_date: req.start_date || "",
    end_date: req.end_date || "",
    request_type: req.request_type || "",
    comments: req.comments || "",
    status: req.status || "",
  });
  const [busy, setBusy] = useState(false);

  const close = () => { setMode(null); setReason(""); setConfirmText(""); };

  const submitEdit = async () => {
    if (!reason.trim()) { toast.error("Motivo obligatorio"); return; }
    const body = { reason: reason.trim() };
    for (const k of ["start_date", "end_date", "request_type", "comments"]) {
      if (edit[k] !== req[k]) body[k] = edit[k];
    }
    if (isSuperAdmin && edit.status !== req.status) body.status = edit.status;
    setBusy(true);
    try {
      await axiosAuth().put(`/vacation-requests/${req.id}/admin-edit`, body);
      toast.success("Solicitud editada"); close(); onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al editar");
    } finally { setBusy(false); }
  };

  const submitCancel = async () => {
    if (!reason.trim()) { toast.error("Motivo obligatorio"); return; }
    setBusy(true);
    try {
      await axiosAuth().put(`/vacation-requests/${req.id}/cancel?reason=${encodeURIComponent(reason.trim())}`);
      toast.success("Solicitud cancelada (admin)"); close(); onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cancelar");
    } finally { setBusy(false); }
  };

  const submitDelete = async () => {
    // Iter 203 (P1) — UX simplificada: pedimos solo "ELIMINAR" (trim + insensitive)
    const tok = (confirmText || "").trim().toUpperCase();
    if (!reason.trim() || reason.trim().length < 10) { toast.error("Motivo mínimo 10 caracteres"); return; }
    if (tok !== "ELIMINAR") { toast.error("Escribe ELIMINAR para confirmar"); return; }
    setBusy(true);
    try {
      const params = new URLSearchParams({
        confirm: "ELIMINAR", reason: reason.trim(), soft: "true",
      }).toString();
      await axiosAuth().delete(`/vacation-requests/${req.id}?${params}`);
      toast.success("Solicitud eliminada (soft delete)"); close(); onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al eliminar");
    } finally { setBusy(false); }
  };

  const reasonOptions = [
    "Error de carga", "Solicitud duplicada", "Cambio solicitado por empleado",
    "Corrección administrativa", "Otro",
  ];

  return (
    <>
      <Button size="sm" variant="outline" className="text-xs rounded-sm" onClick={() => setMode("edit")} data-testid="admin-edit-vacation"><Edit3 className="w-3 h-3 mr-1" /> Editar (admin)</Button>
      <Button size="sm" variant="outline" className="text-xs rounded-sm text-amber-700 border-amber-400" onClick={() => setMode("cancel")} data-testid="admin-cancel-vacation"><X className="w-3 h-3 mr-1" /> Cancelar / Anular</Button>
      <Button size="sm" variant="outline" className="text-xs rounded-sm text-red-700 border-red-400" onClick={() => setMode("delete")} data-testid="admin-delete-vacation"><Trash2 className="w-3 h-3 mr-1" /> Eliminar</Button>

      {/* EDITAR */}
      <Dialog open={mode === "edit"} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent className="max-w-lg" data-testid="admin-edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Edit3 className="w-4 h-4" /> Editar solicitud (admin)</DialogTitle>
            <DialogDescription>Empleado: <strong>{req.employee_name}</strong>. Cualquier cambio se audita.</DialogDescription>
          </DialogHeader>
          {req.status === "aprobada" && (
            <div className="text-[11px] bg-amber-50 border border-amber-200 rounded-sm p-2 flex gap-1 items-start" data-testid="admin-edit-approved-warn">
              <AlertTriangle className="w-3 h-3 text-amber-600 mt-0.5" />
              <span>Esta solicitud ya estaba aprobada. El cambio recalculará saldo y planificación.</span>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div><label className="text-[10px] uppercase">Inicio</label><Input type="date" value={edit.start_date} onChange={(e) => setEdit({ ...edit, start_date: e.target.value })} className="h-8 rounded-sm" data-testid="admin-edit-start" /></div>
            <div><label className="text-[10px] uppercase">Fin</label><Input type="date" value={edit.end_date} onChange={(e) => setEdit({ ...edit, end_date: e.target.value })} className="h-8 rounded-sm" data-testid="admin-edit-end" /></div>
            <div className="col-span-2"><label className="text-[10px] uppercase">Tipo</label><Input value={edit.request_type} onChange={(e) => setEdit({ ...edit, request_type: e.target.value })} className="h-8 rounded-sm font-mono" data-testid="admin-edit-type" /></div>
            <div className="col-span-2"><label className="text-[10px] uppercase">Comentarios</label><Textarea value={edit.comments} onChange={(e) => setEdit({ ...edit, comments: e.target.value })} className="text-xs rounded-sm" /></div>
            {isSuperAdmin && (
              <div className="col-span-2"><label className="text-[10px] uppercase text-purple-700">Estado (override super_admin)</label><Input value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })} className="h-8 rounded-sm font-mono" data-testid="admin-edit-status" /></div>
            )}
            <div className="col-span-2"><label className="text-[10px] uppercase font-bold">Motivo *</label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Corrección administrativa de fechas…" className="h-8 rounded-sm" data-testid="admin-edit-reason" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close} data-testid="admin-edit-cancel">Cancelar</Button>
            <Button onClick={submitEdit} disabled={busy} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="admin-edit-save">Guardar cambios</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CANCELAR */}
      <Dialog open={mode === "cancel"} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent className="max-w-md" data-testid="admin-cancel-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700"><X className="w-4 h-4" /> Cancelar / Anular solicitud</DialogTitle>
            <DialogDescription>Si estaba aprobada, libera el saldo. Quedará marcada como <code>cancelada_admin</code> y visible en historial.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-xs">
            <label className="text-[10px] uppercase font-bold">Motivo *</label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="h-8 rounded-sm" data-testid="admin-cancel-reason-select"><SelectValue placeholder="Elige motivo…" /></SelectTrigger>
              <SelectContent>{reasonOptions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
            {reason === "Otro" && <Input placeholder="Describe el motivo" onChange={(e) => setReason(e.target.value)} className="h-8 rounded-sm" data-testid="admin-cancel-reason-other" />}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close} data-testid="admin-cancel-back">Volver</Button>
            <Button onClick={submitCancel} disabled={busy || !reason.trim()} className="bg-amber-700 hover:bg-amber-800 text-white" data-testid="admin-cancel-confirm">Confirmar cancelación</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ELIMINAR (soft delete con doble confirmación) */}
      <Dialog open={mode === "delete"} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent className="max-w-md" data-testid="admin-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700"><Trash2 className="w-4 h-4" /> Eliminar solicitud</DialogTitle>
            <DialogDescription>Esta acción eliminará la solicitud del sistema. Si estaba aprobada, se recalculará el saldo y la planificación. Esta acción quedará auditada.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-xs">
            <label className="text-[10px] uppercase font-bold">Motivo (mínimo 10 caracteres) *</label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Solicitud duplicada por error administrativo" className="h-8 rounded-sm" data-testid="admin-delete-reason" />
            <label className="text-[10px] uppercase font-bold text-red-700">
              Escribe <code className="bg-red-50 px-1 rounded font-mono text-red-800">ELIMINAR</code> para confirmar
            </label>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="ELIMINAR"
              className="h-8 rounded-sm font-mono uppercase"
              data-testid="admin-delete-confirm-text"
              autoComplete="off"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close} data-testid="admin-delete-back">Volver</Button>
            <Button
              onClick={submitDelete}
              disabled={busy || (confirmText || "").trim().toUpperCase() !== "ELIMINAR" || reason.trim().length < 10}
              className="bg-red-700 hover:bg-red-800 text-white"
              data-testid="admin-delete-confirm"
            >Eliminar definitivamente</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
